// src/lib/managers/conversation/ConversationManager.ts

import { EventBus } from "../../contexts";
import {
    filterNoReplyContactsForQuery,
    isNoReplyAddress,
    logger,
    LOGS,
} from "../../utils";
import { buildCountMessage } from "../../utils/countMessage";
import { parseGraphDateTimeToIsoString } from "../../utils/date/format-time";
import { optimizeMailBody } from "../../utils/textProcessing/textProcessing";
import { generateUUID } from "../../utils/uuid/generateUUID";
import {
    AgendaFilter,
    AgendaItem,
    AgendaItemDateTime,
    AgendaManager,
    AgendaTimeSlot,
    CreateAgendaItemData,
} from "../agenda";
import { DEFAULT_SLOT_MINUTES } from "../agenda/freeSlots";
import type {
    ConversationDetailDTO,
    ConversationEtapesMemoireDTO,
    ConversationEtapesSuiteDTO,
    ConversationTurnSaveDTO,
} from "../api/types/apiResponse";
import {
    BestContactResult,
    ContactManager,
    RAG_DEFAULT_TIMEOUT_MS,
    ResolveContactsResult,
} from "../contact";
import { CustomViewSortOrder, ViewFilter } from "../customView";
import { DataManager } from "../dataManager/DataManager";
import { IaManager } from "../ia";
import { VOIX } from "../ia/types/const";
import { Mail, MailManager } from "../mail";
import { uniqueMailvistaId } from "../mail/domain/mailIdentity";
import { UserManager } from "../user";
import { MAILS_ANALYSIS_MAX } from "./constants";
import { buildUnsupportedResponse, readToolCall } from "./contract";
import {
    ACTION_PROPOSAL_VOCAL,
    AGENDA_ITEMS_COMMENT,
    AGENDA_ITEMS_VOCAL,
    FREE_SLOTS_COMMENT,
    FREE_SLOTS_VOCAL,
    MAIL_SEARCH_COMMENT,
    MAIL_SEARCH_VOCAL,
    noMailForUnknownContactsMessage,
} from "./countMessages";
import {
    ANSWER_REF,
    buildLireAgendaResultat,
    compactedContextSize,
    Etape,
    EtapePersonne,
    EtapeResultat,
    ETAPES_MAX_CALLS,
    ETAPES_REPLAY_DELAY_MS,
    EtapesBrouillon,
    EtapesReading,
    isDate,
    LIRE_MAILS_MAX,
    readAssistantUsage,
    readEtapesMemoire,
    readEtapesResponse,
    readModeleUtilise,
    readRaisonnement,
    repliedRef,
    toEtapeMail,
    toEtapeMailEnvoye,
    toEtapePersonne,
} from "./etapes";
import {
    chosenContacts,
    contactOf,
    noteContact,
    readFacts,
    sameFacts,
    withoutFact,
    withoutRemoved,
    type ConversationFact,
} from "./facts";
import {
    allInTrashMessage,
    DELETE_WITHOUT_CRITERIA_MESSAGE,
    excludeTrashForDelete,
    groupActionGuard,
    hasCriteria,
    TRASH_UNKNOWN_MESSAGE,
    trashExcludedMessage,
} from "./guard";
import {
    readHistoryCompaction,
    readHistoryContext,
    readHistoryTurn,
    toHistoryValue,
    type HistoryCompactionPayload,
    type HistoryTurnPayload,
} from "./history";
import {
    buildPeriodMessageResponse,
    INVERTED_PERIOD_MESSAGE,
    PERIOD_IGNORED_MESSAGE,
    readAgendaPeriod,
    readSearchPeriod,
    SearchPeriod,
    UNREADABLE_AGENDA_PERIOD_MESSAGE,
    UNREADABLE_EVENT_DATE_MESSAGE,
    UNREADABLE_PERIOD_FOR_ACTION_MESSAGE,
} from "./period";
import {
    applyMailScope,
    toEtapesProjet,
    type MailScopeOutcome,
} from "./project";
import {
    contactQuestion,
    withChosenContact,
    type ContactQuestion,
    type EtapesQuestion,
} from "./question";
import { buildTextResponse } from "./textResponse";
import {
    buildCancelledResponse,
    buildStoppedResponse,
    buildTurnErrorResponse,
    classifyTurnError,
    isAbort,
    isReplayableCallError,
    TurnCancelledError,
    untilCancelled,
} from "./turnError";
import {
    analysisAgentTools,
    availableTools,
    toolDescriptions,
} from "./types/const";
import {
    AgentInvokeParams,
    AgentModelChoice,
    AnswerFeedback,
    ApplyActionToPreviousResultsParams,
    AssistantAnswer,
    AssistantDraft,
    AssistantProject,
    AssistantProjectInput,
    AssistantUnknownName,
    AssistantUsage,
    BasicAiChatParams,
    ComponentData,
    ComponentDataContentType,
    ContentAction,
    ConversationContextSize,
    ConversationContract,
    ConversationSummary,
    CreateAgendaItemParams,
    FallbackResponseParams,
    FindFreeSlotsParams,
    GetAgendaItemsParams,
    Message,
    MinifiedAgenda,
    MinifiedMail,
    MoreDataCallbackResult,
    ReasoningItem,
    RestoredConversation,
    RestoredConversationItem,
    ResultSetCacheEntry,
    SearchMailsParams,
    SuggestAvailableToolsParams,
    ToolImplementation,
} from "./types/conversation";

// #region Agenda pagination constants (AB#158)
/** Items shown per "load more" step (including the initial display). */
const AGENDA_DISPLAY_BATCH = 6;
/** Graph page / block size buffered in memory, then served AGENDA_DISPLAY_BATCH at a time.
 *  Kept a multiple of AGENDA_DISPLAY_BATCH so every "load more" step serves a full batch. */
const AGENDA_FETCH_BLOCK = 30;
/** $top used when scanning with a client-side attendee filter (larger → fewer round-trips). */
const AGENDA_ATTENDEE_SCAN_PAGE = 100;
/** Hard cap on the events fed to the fire-and-forget analysis agent (AB#158). Bounds BOTH the scan
 *  (round-trips → 429 risk on dense calendars) AND the minified payload (agent token budget). A wider
 *  window may hold far more events; the analysis covers the first N in the requested order. Doubling
 *  as the scan page size means the common (no-attendee) case fetches a single page of exactly N. */
const AGENDA_ANALYSIS_MAX_ITEMS = 50;
/** Safety cap on pages scanned per accumulation (dense calendars) — logged when hit. */
const AGENDA_MAX_SCAN_PAGES = 40;

/** Invariant context threaded through the agenda pagination helpers (kept as one object to
 *  avoid long parameter lists across the recursive "load more" callback). */
type AgendaPaginationContext = {
    reqId: string;
    baseFilter: AgendaFilter;
    includeBody: boolean;
    scanPageSize: number;
};
// #endregion

/**
 * Ce que la conversation a lu (contrat par étapes), par `ref` : l'affichage final le reprend, à ce
 * tour ou à un tour suivant. Une ref par élément sur toute la conversation (« e1 », « m1 »…) : un
 * événement ou un mail rendu par deux lectures garde la ref de la première, d'un tour à l'autre.
 */
type EtapesReads = {
    events: Map<string, AgendaItem>;
    /** Mails lus, avec le filtre et les critères de leur lecture (bloc [ref:Rx …] de l'historique). */
    mails: Map<
        string,
        { mail: Mail; filter: ViewFilter; criteria: SearchMailsParams }
    >;
    /** Ref déjà donnée, par identifiant d'événement (fournisseur) ou de mail (uniqueMailvistaId). */
    refs: Map<string, string>;
};

/** Le pouce gardé avec un tour de l'historique → le pouce affiché ; toute autre valeur : aucun. */
const FEEDBACK_BY_AVIS: Record<string, AnswerFeedback | undefined> = {
    utile: "up",
    pas_utile: "down",
};

const emptyReads = (): EtapesReads => ({
    events: new Map(),
    mails: new Map(),
    refs: new Map(),
});

/** Ce qu'une étape a lu, avant que ses éléments reçoivent leur ref (dans l'ordre des étapes). */
type EtapeLue =
    | { outil: "lireAgenda"; items: AgendaItem[] }
    | {
          outil: "lireMails";
          found: Mail[];
          limit: number;
          filter: ViewFilter;
          criteria: SearchMailsParams;
          people: EtapePersonne[];
          /** Conversation d'un projet : ce que son périmètre a fait de la lecture (project.ts). */
          scope?: MailScopeOutcome;
      }
    // La lecture n'est pas faite : il faut d'abord que l'utilisateur précise un contact.
    | { outil: "lireMails"; question: EtapesQuestion }
    | { erreur: "agenda_illisible" | "boite_illisible" | "etape_inconnue" }
    // La lecture n'est pas faite : un nom n'a pas pu être cherché (RAG et carnet en panne ou trop lents).
    | { erreur: "contacts_indisponibles"; personnes: EtapePersonne[] };

/** Ce que l'app ajoute à la réponse quand elle pose sa question avant de préparer le rendez-vous. */
const ATTENDEE_TO_CONFIRM_NOTE =
    "Avant de préparer le rendez-vous, il me manque une précision sur un participant.";

/**
 * La question d'un tour gardé, au plus ce que l'historique de l'Api-Backend accepte (20 000 caractères,
 * `saveAssistantConversationTurn`) : un long texte collé est coupé, sinon le tour entier serait refusé et
 * manquerait à la conversation rouverte. La question entière reste dans l'échange du tour.
 */
const HISTORY_QUESTION_MAX = 20_000;

export const questionForHistory = (question: string): string => {
    if (question.length <= HISTORY_QUESTION_MAX) return question;
    const cut = question.slice(0, HISTORY_QUESTION_MAX - 1);
    // Jamais la moitié d'un caractère (emoji) : l'Api-Backend compte en unités UTF-16, comme `length`.
    return `${/[\uD800-\uDBFF]$/.test(cut) ? cut.slice(0, -1) : cut}…`;
};

/**
 * Un rendez-vous proposé par l'assistant, en attente le temps de préciser un participant
 * (CONCEPTION-personnes-de-la-conversation de l'Api-Ai, R3) : la fin du tour qui le proposait, et la
 * question posée. L'assistant a fini, seule l'app attend l'adresse à inviter : au clic sur un choix,
 * c'est elle qui termine le rendez-vous, sans rappeler l'Api-Ai.
 */
type PendingAgenda = {
    reading: Extract<EtapesReading, { kind: "reponse" }>;
    reads: EtapesReads;
    question: ContactQuestion;
    /** Le tour qui a proposé le rendez-vous : ses pouces et son modèle valent pour la carte. */
    turnId?: string;
    answerModel?: ComponentData["answerModel"];
};

/** Vrai si l'étape a lu les mails envoyés (boîte « envoyes »), et non la boîte de réception. */
const isSentRead = (lue: EtapeLue): boolean =>
    "criteria" in lue && lue.criteria.boite === "envoyes";

const sameName = (a: string, b: string): boolean =>
    a.trim().toLowerCase() === b.trim().toLowerCase();

/**
 * Les noms introuvables d'une lecture de mails faite (CONCEPTION-nom-introuvable.md de l'Api-Ai) : ceux
 * que les contacts n'ont pas trouvés, et que la lecture a donc cherchés tels quels dans les adresses. Dans
 * les mails envoyés, ou donné seulement comme destinataire, le nom est un destinataire ; sinon un
 * expéditeur. Une lecture non faite (question à l'utilisateur, contacts indisponibles) n'en a pas.
 */
const unknownNamesOf = (lue: EtapeLue): AssistantUnknownName[] => {
    if (!("people" in lue)) return [];
    const { criteria } = lue;
    return lue.people
        .filter((person) => person.resultat === "introuvable")
        .map((person) => {
            const asSender = (criteria.from ?? []).some((name) =>
                sameName(name, person.nom),
            );
            const asRecipient = (criteria.to ?? []).some((name) =>
                sameName(name, person.nom),
            );
            const recipient = isSentRead(lue) || (asRecipient && !asSender);
            return {
                name: person.nom,
                role: recipient ? "recipient" : "sender",
            };
        });
};

/** Ajoute des noms introuvables à ceux du tour : un même nom, au même rôle, n'est dit qu'une fois. */
const addUnknownNames = (
    into: AssistantUnknownName[],
    names: AssistantUnknownName[],
): void => {
    for (const name of names) {
        const known = into.some(
            (other) =>
                other.role === name.role && sameName(other.name, name.name),
        );
        if (!known) into.push(name);
    }
};

/**
 * Vrai si les critères en portent un qui n'existe que pour les mails reçus : expéditeur, vue,
 * catégorie, lu ou non lu (les mêmes que l'Api-Ai, `assistant/domaine.py`).
 */
const hasReceivedOnlyCriteria = (criteria: SearchMailsParams): boolean =>
    (criteria.from?.length ?? 0) > 0 ||
    (criteria.mailStatus?.length ?? 0) > 0 ||
    (criteria.categories?.length ?? 0) > 0 ||
    Boolean(criteria.readStatus);

/** Ce qu'un tour réussi du contrat par étapes laisse à l'historique : son échange et ses lectures. */
type EtapesHistoryTurn = {
    echange: string;
    lectures: HistoryTurnPayload["lectures"];
};

/** Un tour du contrat par étapes : la réponse affichée, et ce que l'historique en garde. */
type EtapesTurnResult = {
    response: ComponentData;
    historyTurn?: EtapesHistoryTurn;
};

/**
 * Un appel perdu (réseau coupé, 502/503/504) est rejoué une fois, tel quel : l'Api-Ai est sans état
 * et les étapes déjà exécutées voyagent dans `suite`. Rien sur un 4xx ni sur un quota.
 */
async function callWithOneReplay<T>(
    callOnce: () => Promise<T>,
    signal: AbortSignal,
): Promise<T> {
    try {
        return await callOnce();
    } catch (error) {
        if (signal.aborted || !isReplayableCallError(error)) throw error;
        logger.warn(
            LOGS.ConversationManager,
            `contrat par étapes - appel perdu, rejoué une fois (${error})`,
        );
        await new Promise((resolve) =>
            setTimeout(resolve, ETAPES_REPLAY_DELAY_MS),
        );
        if (signal.aborted) throw new TurnCancelledError();
        return callOnce();
    }
}

/**
 * La réponse de l'assistant à lire à voix haute : sans ses ref ni les marques de mise en forme (les
 * éléments cités se voient à l'écran).
 */
const spokenAnswer = (text: string): string =>
    text
        .replace(ANSWER_REF, "")
        .replace(/[*_#>`|]/g, "")
        .replace(/[ \t]+/g, " ")
        .replace(/ *\n */g, "\n")
        .trim();

/**
 * F-1.13 : participants que les contacts ne trouvent pas. Le brouillon part sans eux : l'utilisateur
 * les ajoute à la main.
 */
const quotedNames = (names: string[]): string => {
    const quoted = names.map((name) => `« ${name} »`);
    return quoted.length === 1
        ? quoted[0]
        : `${quoted.slice(0, -1).join(", ")} et ${quoted[quoted.length - 1]}`;
};

export const unresolvedAttendeesMessage = (names: string[]): string => {
    if (names.length === 0) return "";
    return names.length === 1
        ? `Je n'ai pas trouvé ${quotedNames(names)} dans vos contacts : ajoutez son adresse à la main.`
        : `Je n'ai pas trouvé ${quotedNames(names)} dans vos contacts : ajoutez leurs adresses à la main.`;
};

/**
 * Des participants que l'app n'a pas pu chercher (contacts injoignables) : ce n'est pas « introuvable »
 * (CONCEPTION-contacts, état indisponible).
 */
export const unavailableAttendeesMessage = (names: string[]): string => {
    if (names.length === 0) return "";
    return names.length === 1
        ? `Je n'ai pas pu chercher ${quotedNames(names)} dans vos contacts : réessayez dans un instant, ou ajoutez son adresse à la main.`
        : `Je n'ai pas pu chercher ${quotedNames(names)} dans vos contacts : réessayez dans un instant, ou ajoutez leurs adresses à la main.`;
};

export class ConversationManager {
    // #region Properties

    private eventBus: EventBus;

    private iaManager: IaManager;

    private mailManager: MailManager;

    // private graphManager?: GraphManager;

    private dataManager: DataManager;

    private userManager: UserManager;

    private agendaManager: AgendaManager;

    private contactManager: ContactManager;

    private currentVoice: VOIX = VOIX.ALTIA;

    private messages: Message[] = [];

    private toolImplementations?: ToolImplementation;

    private singleMessageCache: Array<{
        role: "user" | "assistant";
        content: string;
    }> = [];

    // Cache des result sets affichés (recherches searchMails non vides), keyé par alias "R1", "R2"...
    // Permet à applyActionToPreviousResults d'agir sur les ids EXACTS sans relancer de recherche.
    private readonly resultSetCache: Map<string, ResultSetCacheEntry> =
        new Map();

    // Compteur monotone pour les alias "Rx" — réinitialisé avec le resultSetCache
    private refCounter: number = 0;

    // Contrat avec l'Api-Ai (option du SDK) : v1 par défaut ; « etapes » pour le web en local.
    private readonly conversationContract: ConversationContract;

    // Le tour en cours, annulable : « Nouvelle conversation » l'abandonne (requête en vol comprise),
    // et sa réponse, si elle arrive, n'entre ni dans le fil ni dans l'historique.
    private runningTurn?: AbortController;
    /**
     * Le projet où la conversation en cours est rangée (contrat par étapes), s'il y en a un : ses
     * consignes partent avec chaque appel, ses lectures de mails sont limitées à son périmètre, et la
     * conversation y est rangée à son premier tour. Oublié avec la conversation.
     */
    private project?: AssistantProject;
    /** Tours en route vers l'historique, par identifiant : un pouce attend que le sien y soit. */
    private pendingTurnSaves = new Map<string, Promise<void>>();
    private conversationGeneration = 0;
    // Le modèle choisi par l'utilisateur (contrat par étapes) ; « auto » : l'Api-Ai décide.
    private agentModel: AgentModelChoice = "auto";

    // Mémoire de la conversation (contrat par étapes, CONCEPTION-memoire-conversation.md de l'Api-Ai) :
    // les échanges écrits par l'Api-Ai, gardés tels quels et renvoyés à chaque appel ; la place
    // occupée (jauge) ; les lectures, une ref par élément sur toute la conversation. Remis à zéro
    // avec la conversation.
    private exchanges: string[] = [];
    /** La question sur un contact posée au dernier tour : la réponse cliquée fixe le contact. */
    private pendingContactQuestion?: ContactQuestion;
    /** Les questions sur un contact préparées pendant le tour en cours (une seule sera posée). */
    private askedContactQuestions: ContactQuestion[] = [];
    /** Le rendez-vous en attente d'un participant à préciser (R3) ; tout autre message l'abandonne. */
    private pendingAgenda?: PendingAgenda;
    /**
     * Résolutions de contacts de la conversation, par nom : un nom n'est cherché qu'une fois, pas à chaque
     * tour qui le nomme (audit de performance du 15/10, P3-07). Un nom que l'app n'a pas pu chercher
     * (`unavailable`) est cherché à nouveau au tour suivant (`unavailableContacts`).
     */
    private contactResolutions = new Map<string, Promise<BestContactResult>>();
    private unavailableContacts = new Set<string>();
    /**
     * Les faits retenus dans la conversation (facts.ts) : envoyés avec chaque appel, remplacés par ceux
     * que rend la fin du tour ou le compactage, gardés avec la conversation dans l'historique.
     */
    private facts: ConversationFact[] = [];
    /** Les faits tels que l'historique les garde : ils n'y repartent que s'ils ont changé. */
    private savedFacts: ConversationFact[] = [];
    /** Vrai dès que l'historique connaît la conversation (un tour gardé, ou une conversation rouverte). */
    private conversationSaved = false;
    /** Les faits que l'utilisateur a fait oublier pendant le tour en cours : sa fin ne les ramène pas. */
    private forgottenDuringTurn: string[] = [];
    /**
     * Un envoi des faits est en route : le suivant attend sa fin (`factsToSend`), pour que l'historique
     * reçoive les listes dans l'ordre, la dernière en dernier.
     */
    private sendingFacts = false;
    private factsToSend = false;
    private contextSize?: ConversationContextSize;
    /** Ce que l'assistant a consommé (la conversation et le mois), relu à l'Api-Backend après chaque tour. */
    private usage?: AssistantUsage;
    /** Numéro de la dernière relecture de la consommation : une réponse plus ancienne arrivée après est ignorée. */
    private usageReads = 0;
    /** Historique (contrat par étapes) : la conversation en cours ; « Nouvelle conversation » en ouvre une autre. */
    private conversationId: string = generateUUID();
    private reads: EtapesReads = emptyReads();

    // #endregion Properties

    constructor(
        eventBus: EventBus,
        iaManager: IaManager,
        mailManager: MailManager,
        dataManager: DataManager,
        userManager: UserManager,
        agendaManager: AgendaManager,
        contactManager: ContactManager,
        conversationContract: ConversationContract = "v1",
    ) {
        logger.info(
            LOGS.ConversationManager,
            `(contrat ${conversationContract})`,
        );
        this.eventBus = eventBus;
        this.iaManager = iaManager;
        this.mailManager = mailManager;
        this.dataManager = dataManager;
        this.userManager = userManager;
        this.agendaManager = agendaManager;
        this.contactManager = contactManager;
        this.conversationContract = conversationContract;

        this.toolImplementations = this.TOOL_IMPLEMENTATIONS;
        this.messages = [];
    }

    // #region Initialisation and cleanup
    /**
     * @method cleanup
     * Cleans up all resources and stops all active processes for the ConversationManager
     * ═══════════════════════════════════════════════════════════════════════════
     * @input {void}
     * @output {Promise<void>}
     * @public
     */
    public async _cleanup(): Promise<void> {
        logger.info(
            LOGS.ConversationManager,
            "ConversationManager.cleanup() - Starting cleanup process",
        );

        // Phase 1: Stop all active processes and timers
        try {
            logger.info(
                LOGS.ConversationManager,
                "  Phase 1: Stopping active processes",
            );

            // No timers, intervals, or polling to stop for ConversationManager
            // All operations are request-based
        } catch (error) {
            logger.error(
                LOGS.ConversationManager,
                `ConversationManager.cleanup() - Error in Phase 1: ${error}`,
            );
        }

        // Phase 2: Clear internal state and caches
        try {
            logger.info(
                LOGS.ConversationManager,
                "  Phase 2: Clearing internal state",
            );

            // Clear conversation messages history
            this.messages = [];

            // Tour en cours abandonné, cache des messages, result sets et mémoire de la conversation
            this.resetConversationState();

            // Reset voice to default
            this.currentVoice = VOIX.ALTIA;

            // Clear tool implementations
            this.toolImplementations = undefined;
        } catch (error) {
            logger.error(
                LOGS.ConversationManager,
                `ConversationManager.cleanup() - Error in Phase 2: ${error}`,
            );
        }

        logger.info(
            LOGS.ConversationManager,
            "ConversationManager.cleanup() - Cleanup completed successfully",
        );
    }

    // #endregion Initialisation and cleanup

    // #region Public Methods

    /**
     * Récupère les messages de la conversation
     * @returns La liste des messages
     */
    public getMessages(): Message[] {
        return [...this.messages];
    }

    /**
     * Vrai si la conversation a une mémoire mesurée (contrat par étapes) : la jauge et « Compacter »
     * remplacent la limite d'échanges de l'app.
     */
    public hasConversationMemory(): boolean {
        return this.conversationContract === "etapes";
    }

    /**
     * Le modèle de l'assistant choisi par l'utilisateur (contrat par étapes), pour les tours suivants :
     * un tour en cours garde celui de son début.
     */
    public setAgentModel(choice: AgentModelChoice): void {
        this.agentModel = choice;
    }

    public getAgentModel(): AgentModelChoice {
        return this.agentModel;
    }

    /** Place occupée par la conversation, mesurée au dernier tour ; inconnue : `undefined`. */
    public getContextSize(): ConversationContextSize | undefined {
        return this.contextSize ? { ...this.contextSize } : undefined;
    }

    /** Ce que l'assistant a consommé, relu après le dernier tour ; inconnu : `undefined`. */
    public getUsage(): AssistantUsage | undefined {
        return this.usage ? { ...this.usage } : undefined;
    }

    /** Les faits retenus dans la conversation, du plus ancien au plus récent. */
    public getFacts(): ConversationFact[] {
        return this.facts.map((fact) => ({ ...fact }));
    }

    /**
     * « Oublier » un fait retenu (l'utilisateur, à l'écran) : il sort de la liste tout de suite, et de
     * l'historique si la conversation y est. Un contact oublié sera redemandé.
     */
    public forgetFact(key: string): void {
        const remaining = withoutFact(this.facts, key);
        if (remaining.length === this.facts.length) return;
        if (this.runningTurn) this.forgottenDuringTurn.push(key);
        this.setFacts(remaining);
        this.persistFacts();
    }

    /** Vrai s'il y a quelque chose à compacter : au moins deux échanges, et aucun tour en cours. */
    public canCompactConversation(): boolean {
        return (
            this.hasConversationMemory() &&
            this.exchanges.length >= 2 &&
            !this.runningTurn
        );
    }

    /**
     * « Compacter » (contrat par étapes) : l'Api-Ai résume la conversation, et le résumé remplace tous
     * ses échanges ; le fil affiché ne change pas, la place redevient inconnue jusqu'au tour suivant.
     * Rend le résumé en texte, pour l'afficher (« ce que l'assistant garde en mémoire »). Rien à
     * compacter, tour en cours, conversation remise à zéro entre-temps ou échec : `null`, la
     * conversation reste telle quelle.
     */
    public async compactConversation(): Promise<{ summary: string } | null> {
        if (!this.canCompactConversation()) return null;
        const generation = this.conversationGeneration;
        const memoire = this.conversationMemory();
        try {
            const response = await this.iaManager.compactConversation(memoire, {
                traceId: generateUUID(),
                conversationId: this.conversationId,
            });
            const { echange, faits } = readEtapesMemoire(response);
            if (!echange || generation !== this.conversationGeneration) {
                return null;
            }
            // Le compactage a revu les faits : il n'en fait que retirer, et ce qui a changé entre-temps
            // (un fait oublié, un contact choisi par un tour) reste tel quel.
            if (faits) {
                this.setFacts(
                    withoutRemoved(this.facts, memoire.faits ?? [], faits),
                );
            }
            // Un tour terminé pendant le compactage garde son échange, après le résumé.
            this.exchanges = [
                echange,
                ...this.exchanges.slice(memoire.echanges.length),
            ];
            // La jauge ne reste pas vide : la place estimée de la conversation compactée.
            this.setContextSize(
                compactedContextSize(
                    this.contextSize,
                    response.usage?.output_tokens,
                ),
            );
            this.saveCompactionToHistory(
                echange,
                typeof response.resume === "string" ? response.resume : "",
            );
            this.refreshUsage();
            logger.info(
                LOGS.ConversationManager,
                `compactConversation() - ${memoire.echanges.length} échanges remplacés par un résumé`,
            );
            return {
                summary:
                    typeof response.resume === "string" ? response.resume : "",
            };
        } catch (error) {
            logger.error(
                LOGS.ConversationManager,
                `compactConversation() - échec, conversation gardée telle quelle : ${error}`,
            );
            return null;
        }
    }

    public setVoice(voice: VOIX): void {
        logger.info(LOGS.ConversationManager, `setVoice(${voice})`);
        this.currentVoice = voice;
    }

    public async textToSpeech(text: string): Promise<boolean> {
        logger.info(LOGS.ConversationManager, `textToSpeech()`);

        // if (this.currentVoice === VOIX.R2D2) {
        //   return this.r2d2TextToSpeech(text);
        // }

        return this.iaManager.elevenLabsTextToSpeech(text, this.currentVoice);
    }

    /**
     * Traite un message utilisateur unique et génère une réponse sans conserver d'historique
     * @param message - Le message de l'utilisateur
     * @returns La réponse générée ou le résultat de l'outil. Ne lève pas : un tour raté rend une
     * réponse `Error` avec sa cause (`errorCause`) et ne laisse rien dans l'historique (F-1.17).
     */
    public async handleSingleMessage(message: string): Promise<ComponentData> {
        logger.info(
            LOGS.ConversationManager,
            `handleSingleMessage(${message})`,
        );

        // Question de ce tour : retirée du cache si le tour échoue (F-1.17).
        const userEntry: { role: "user"; content: string } = {
            role: "user",
            content: message,
        };
        // Identifiant du tour, transmis à l'Api-Ai en X-Mv-Trace-Id (F-1.24).
        const traceId = generateUUID();
        // Le clic sur un choix de la question posée avant un rendez-vous : l'app le termine (R3).
        const pendingAgenda = this.takePendingAgenda(message);
        this.startContactTurn(message);
        // Ce tour appartient à la conversation en cours ; « Nouvelle conversation » l'annule.
        this.runningTurn?.abort();
        const turn = new AbortController();
        this.runningTurn = turn;
        this.forgottenDuringTurn = [];
        const generation = this.conversationGeneration;
        const cancelled = () => generation !== this.conversationGeneration;

        try {
            // Ajouter le message utilisateur au cache
            this.singleMessageCache.push(userEntry);

            // Vérifie si l'IA veut exécuter une fonction
            if (this.toolImplementations) {
                let functionResponse: ComponentData;
                // Ce que l'historique garde du tour (contrat par étapes seulement).
                let historyTurn: EtapesHistoryTurn | undefined;
                if (pendingAgenda) {
                    // Aucun appel à l'Api-Ai, donc aucun échange à garder pour ce tour.
                    functionResponse = await this.finishPendingAgenda(
                        pendingAgenda,
                        this.toolImplementations,
                    );
                } else if (this.conversationContract === "etapes") {
                    // Contrat par étapes (option du SDK, le web en local) : plusieurs appels par tour ;
                    // la fin du tour est un affichage, ou un `toolCall` v1 traité comme en v1.
                    ({ response: functionResponse, historyTurn } =
                        await this.runEtapesTurn(
                            traceId,
                            this.toolImplementations,
                            turn.signal,
                        ));
                } else {
                    // Premier appel via IaManager
                    const response =
                        await this.iaManager.processConversationWithAI(
                            this.singleMessageCache,
                            availableTools,
                            { traceId },
                        );

                    logger.info(
                        LOGS.ConversationManager,
                        `Response from IaManager`,
                        response,
                    );

                    functionResponse = await this.executeToolCall(
                        response,
                        this.toolImplementations,
                    );
                }

                // Conversation remise à zéro pendant le tour : sa réponse n'a plus de place nulle part.
                if (cancelled()) {
                    return buildCancelledResponse();
                }
                // « Arrêter » pendant le tour : la réponse arrivée entre-temps n'est pas montrée.
                if (turn.signal.aborted) {
                    this.removeFailedTurn(userEntry);
                    return buildStoppedResponse();
                }

                // Outil en échec (Graph, Google, Api-Backend) : tour raté, rien dans l'historique et
                // la question en sort (F-1.17).
                if (
                    functionResponse.content.contentType ===
                    ComponentDataContentType.Error
                ) {
                    this.removeFailedTurn(userEntry);
                    return {
                        ...functionResponse,
                        errorCause: functionResponse.errorCause ?? "service",
                    };
                }

                // Mise en forme du message a enregistrer dans l'historique
                const aiMessage = this.historyText(functionResponse);
                this.saveTurnToHistory(
                    message,
                    traceId,
                    functionResponse,
                    historyTurn,
                );

                // Ajouter la réponse de l'IA au cache
                this.singleMessageCache.push({
                    role: "assistant",
                    content: aiMessage,
                });

                logger.info(
                    LOGS.ConversationManager,
                    "Conversation Function Response : ",
                    functionResponse,
                );

                return functionResponse;
            } else {
                logger.error(
                    LOGS.ConversationManager,
                    `toolImplementations n'a pas été initialisé`,
                );
                throw "toolImplementations n'a pas été initialisé";
            }
        } catch (error) {
            // Tour annulé : rien à dire, rien à retirer (l'historique a été vidé avec la conversation).
            if (cancelled()) {
                return buildCancelledResponse();
            }
            // Tour arrêté (« Arrêter ») : la conversation reste, la question en sort.
            if (isAbort(error)) {
                this.removeFailedTurn(userEntry);
                return buildStoppedResponse();
            }
            // Tour raté (F-1.17) : l'erreur reste hors de l'historique — elle repartait vers le
            // modèle au tour suivant —, la question en sort, et la réponse donne la cause au lieu
            // de lever.
            const cause = classifyTurnError(error);
            logger.error(
                LOGS.ConversationManager,
                `Error in handleSingleMessage (tour ${traceId}, cause ${cause}): ${error}`,
            );

            this.removeFailedTurn(userEntry);

            return buildTurnErrorResponse(cause);
        } finally {
            if (this.runningTurn === turn) this.runningTurn = undefined;
        }
    }

    /**
     * Reset le cache des messages de handleSingleMessage
     */
    public resetSingleMessageCache(): void {
        logger.info(LOGS.ConversationManager, `resetSingleMessageCache()`);
        this.resetConversationState();
    }

    /**
     * Récupère le cache des messages de handleSingleMessage
     * @returns Le cache des messages
     */
    public getSingleMessageCache(): Array<{
        role: "user" | "assistant";
        content: string;
    }> {
        return [...this.singleMessageCache];
    }

    // #endregion Public Methods

    // #region Private Methods

    private TOOL_IMPLEMENTATIONS = {
        /**
         * @function searchMails
         * @description Recherche des emails selon différents critères
         */
        searchMails: async (
            args: SearchMailsParams,
        ): Promise<ComponentData> => {
            // Create user request id
            const userRequestId: string = generateUUID();

            try {
                const componentData: ComponentData =
                    await this.executeSearchMailsImplementation(args);
                return componentData;
            } catch (error) {
                logger.error(
                    LOGS.ConversationManager,
                    `💥 Error in searchMails: ${error}`,
                );
                return {
                    aiComment: `Je suis désolé, une erreur est survenue lors de la recherche de vos emails.`,
                    userRequestId: userRequestId,
                    content: {
                        contentData: [],
                        contentType: ComponentDataContentType.Error,
                        contentAction: ContentAction.VIEW,
                        upcomingAnalysis: false,
                    },
                    aiVocalText: `Je suis désolé, une erreur est survenue lors de la recherche de vos emails.`,
                    errorCause: classifyTurnError(error),
                };
            }
        },

        /**
         * @function fallbackResponse
         * @description Génère une réponse de secours lorsqu'aucune action spécifique n'est possible
         */
        fallbackResponse: (args: FallbackResponseParams): ComponentData => {
            // Arguments illisibles (contrat v1 tolérant, F-1.17) : réponse texte, pas d'exception.
            try {
                const componentData: ComponentData =
                    this.executeFallbackResponseImplementation(args);
                return componentData;
            } catch (error) {
                return this.unsupportedTextToolResponse(
                    "fallbackResponse",
                    error,
                );
            }
        },

        /**
         * @function suggestAvailableTools
         * @description Génère une liste de suggestions d'outils disponibles avec leurs descriptions
         */
        suggestAvailableTools: (
            args: SuggestAvailableToolsParams,
        ): ComponentData => {
            // Arguments illisibles (contrat v1 tolérant, F-1.17) : réponse texte, pas d'exception.
            try {
                const componentData: ComponentData =
                    this.executeSuggestToolsImplementation(args);
                return componentData;
            } catch (error) {
                return this.unsupportedTextToolResponse(
                    "suggestAvailableTools",
                    error,
                );
            }
        },

        /**
         * @function basicAiChat
         * @description Génère une réponse simple de chat sans contenu spécifique
         */
        basicAiChat: (args: BasicAiChatParams): ComponentData => {
            // Arguments illisibles (contrat v1 tolérant, F-1.17) : réponse texte, pas d'exception.
            try {
                const componentData: ComponentData =
                    this.executeBasicChatImplementation(args);
                return componentData;
            } catch (error) {
                return this.unsupportedTextToolResponse("basicAiChat", error);
            }
        },

        /**
         * @function createAgendaItem
         * @description Prépare les données pour créer un nouvel événement dans le calendrier
         */
        createAgendaItem: async (
            args: CreateAgendaItemParams,
        ): Promise<ComponentData> => {
            // Create user request id
            const userRequestId: string = generateUUID();

            try {
                const componentData: ComponentData =
                    await this.executeCreateAgendaItemImplementation(args);
                return componentData;
            } catch (error) {
                logger.error(
                    LOGS.ConversationManager,
                    `💥 Error in createAgendaItem: ${error}`,
                );
                return {
                    aiComment: `Je suis désolé, une erreur est survenue lors de la préparation de votre événement.`,
                    userRequestId: userRequestId,
                    content: {
                        contentData: "",
                        contentType: ComponentDataContentType.Error,
                        contentAction: ContentAction.VIEW,
                        upcomingAnalysis: false,
                    },
                    aiVocalText: `Je suis désolé, une erreur est survenue lors de la préparation de votre événement.`,
                    errorCause: classifyTurnError(error),
                };
            }
        },

        /**
         * @function getAgendaItems
         * @description Récupère les événements du calendrier pour une période donnée
         */
        getAgendaItems: async (
            args: GetAgendaItemsParams,
        ): Promise<ComponentData> => {
            // Create user request id
            const userRequestId: string = generateUUID();

            try {
                const componentData: ComponentData =
                    await this.executeGetAgendaItemsImplementation(args);
                return componentData;
            } catch (error) {
                logger.error(
                    LOGS.ConversationManager,
                    `💥 Error in getAgendaItems: ${error}`,
                );
                return {
                    aiComment: `Je suis désolé, une erreur est survenue lors de la récupération de votre agenda.`,
                    userRequestId: userRequestId,
                    content: {
                        contentData: [],
                        contentType: ComponentDataContentType.Error,
                        contentAction: ContentAction.VIEW,
                        upcomingAnalysis: false,
                    },
                    aiVocalText: `Je suis désolé, une erreur est survenue lors de la récupération de votre agenda.`,
                    errorCause: classifyTurnError(error),
                };
            }
        },

        /**
         * @function findFreeTimeSlots
         * @description Recherche des créneaux libres dans l'agenda pour une période donnée
         */
        findFreeTimeSlots: async (
            args: FindFreeSlotsParams,
        ): Promise<ComponentData> => {
            // Create user request id
            const userRequestId: string = generateUUID();

            try {
                const componentData: ComponentData =
                    await this.executeFindFreeSlotsImplementation(args);
                return componentData;
            } catch (error) {
                logger.error(
                    LOGS.ConversationManager,
                    `💥 Error in findFreeTimeSlots: ${error}`,
                );
                return {
                    aiComment: `Je suis désolé, une erreur est survenue lors de la recherche de vos créneaux libres.`,
                    userRequestId: userRequestId,
                    content: {
                        contentData: [],
                        contentType: ComponentDataContentType.Error,
                        contentAction: ContentAction.VIEW,
                        upcomingAnalysis: false,
                    },
                    aiVocalText: `Je suis désolé, une erreur est survenue lors de la recherche de vos créneaux libres.`,
                    errorCause: classifyTurnError(error),
                };
            }
        },

        /**
         * @function applyActionToPreviousResults
         * @description Applique une action sur un result set précédemment affiché (référencé
         * par son alias "Rx") sans relancer de recherche — agit sur les ids exacts mémorisés
         */
        applyActionToPreviousResults: async (
            args: ApplyActionToPreviousResultsParams,
        ): Promise<ComponentData> => {
            // Create user request id (réutilisé en cas de succès comme d'erreur)
            const userRequestId: string = generateUUID();

            try {
                const componentData: ComponentData =
                    await this.executeApplyActionToPreviousResultsImplementation(
                        args,
                        userRequestId,
                    );
                return componentData;
            } catch (error) {
                logger.error(
                    LOGS.ConversationManager,
                    `💥 Error in applyActionToPreviousResults: ${error}`,
                );
                return {
                    aiComment: `Je suis désolé, une erreur est survenue lors de l'application de l'action sur vos emails.`,
                    userRequestId: userRequestId,
                    content: {
                        contentData: [],
                        contentType: ComponentDataContentType.Error,
                        contentAction: ContentAction.VIEW,
                        upcomingAnalysis: false,
                    },
                    aiVocalText: `Je suis désolé, une erreur est survenue lors de l'application de l'action sur vos emails.`,
                    errorCause: classifyTurnError(error),
                };
            }
        },
    };

    private executeFallbackResponseImplementation(
        args: FallbackResponseParams,
    ): ComponentData {
        // Create user request id
        const userRequestId: string = generateUUID();

        // Champs absents (contrat v1 tolérant, F-1.17) : texte vide, jamais « undefined » à
        // l'écran, à voix haute ou dans l'historique.
        const aiComment = args.aiComment ?? "";
        const aiSuggestion = args.aiSuggestion ?? "";

        return buildTextResponse(aiComment, {
            suggestion: aiSuggestion,
            userRequestId,
        });
    }

    private executeBasicChatImplementation(
        args: BasicAiChatParams,
    ): ComponentData {
        // Create user request id
        const userRequestId: string = generateUUID();

        // `aiComment` absent (contrat v1 tolérant, F-1.17) : texte vide. Une entrée d'historique
        // sans texte ferait échouer tous les tours suivants.
        const aiComment = args.aiComment ?? "";

        return {
            aiComment: aiComment,
            userRequestId: userRequestId,
            content: {
                contentData: "",
                contentType: ComponentDataContentType.BasicAiChat,
                contentAction: ContentAction.VIEW,
                upcomingAnalysis: false,
            },
            aiVocalText: aiComment,
        };
    }

    private async executeFindFreeSlotsImplementation(
        args: FindFreeSlotsParams,
    ): Promise<ComponentData> {
        // Create user request id
        const userRequestId: string = generateUUID();

        // Dates (F-1.9) : période illisible → message, sans recherche ; période inversée → remise
        // dans l'ordre, et écrite dans les logs.
        const period = readAgendaPeriod(args.startDate, args.endDate);
        if (!period) {
            logger.warn(
                LOGS.ConversationManager,
                `executeFindFreeSlotsImplementation() - date illisible : message`,
            );
            return buildPeriodMessageResponse(UNREADABLE_AGENDA_PERIOD_MESSAGE);
        }
        if (period.isInverted) {
            logger.warn(
                LOGS.ConversationManager,
                `executeFindFreeSlotsImplementation() - startDate > endDate détecté, inversion automatique`,
            );
        }
        const { start: startDate, end: endDate } = period;

        // console.log('📅 Searching free slots:', {
        //   startDate: args.startDate,
        //   endDate: args.endDate,
        //   duration: args.minutesToAllocate
        // });
        // Recherche des créneaux libres (durée absente : créneaux d'une heure)
        const minutes = args.minutesToAllocate ?? DEFAULT_SLOT_MINUTES;
        const freeSlots = await this.agendaManager.findFreeTimeSlots(
            startDate,
            endDate,
            minutes,
        );

        const durationParams = {
            duration: this.formatDuration(minutes),
        };
        const fallbackSlotsComment = buildCountMessage(
            freeSlots.length,
            FREE_SLOTS_COMMENT,
            durationParams,
        );
        const responseMessage =
            freeSlots.length === 0
                ? fallbackSlotsComment
                : args.aiComment || fallbackSlotsComment;
        const vocalMessage = buildCountMessage(
            freeSlots.length,
            FREE_SLOTS_VOCAL,
            durationParams,
        );

        return this._buildFreeSlotsComponentData(
            userRequestId,
            freeSlots,
            responseMessage,
            vocalMessage,
        );
    }

    /**
     * Assemble le ComponentData d'une réponse créneaux (contentType FreeTimeSlots) : 5 créneaux,
     * puis les suivants 5 par 5 (« voir plus »). Pour `findFreeTimeSlots`.
     */
    private _buildFreeSlotsComponentData(
        userRequestId: string,
        freeSlots: AgendaTimeSlot[],
        responseMessage: string,
        vocalMessage: string,
    ): ComponentData {
        const BATCH_SIZE = 5;

        const buildBatchCallback = (
            allSlots: AgendaTimeSlot[],
            offset: number,
            reqId: string,
        ): (() => Promise<MoreDataCallbackResult>) | undefined => {
            if (offset >= allSlots.length) return undefined;
            return async () => ({
                userRequestId: reqId,
                content: allSlots.slice(offset, offset + BATCH_SIZE),
                moreDataCallBack: buildBatchCallback(
                    allSlots,
                    offset + BATCH_SIZE,
                    reqId,
                ),
            });
        };

        const initialSlots = freeSlots.slice(0, BATCH_SIZE);

        // Formation de la réponse avec les créneaux trouvés
        const componentData: ComponentData = {
            aiComment: responseMessage,
            userRequestId: userRequestId,
            content: {
                contentData: initialSlots,
                contentType: ComponentDataContentType.FreeTimeSlots,
                contentAction: ContentAction.VIEW,
                upcomingAnalysis: false,
                moreDataCallBack: buildBatchCallback(
                    freeSlots,
                    BATCH_SIZE,
                    userRequestId,
                ),
            },
            aiVocalText: vocalMessage,
        };

        // console.log(componentData);
        return componentData;
    }

    private async executeGetAgendaItemsImplementation(
        args: GetAgendaItemsParams,
    ): Promise<ComponentData> {
        // Create user request id
        const userRequestId: string = generateUUID();

        // Dates (F-1.9) : période illisible → message, sans lecture de l'agenda.
        const period = readAgendaPeriod(args.startDate, args.endDate);
        if (!period) {
            logger.warn(
                LOGS.ConversationManager,
                `executeGetAgendaItemsImplementation() - date illisible : message`,
            );
            return buildPeriodMessageResponse(UNREADABLE_AGENDA_PERIOD_MESSAGE);
        }

        // Correction si l'IA a inversé les dates : `period` les rend dans l'ordre.
        if (period.isInverted) {
            logger.warn(
                LOGS.ConversationManager,
                `executeGetAgendaItemsImplementation() - startDate > endDate détecté, inversion automatique`,
            );
        }
        const { start: startDate, end: endDate } = period;

        const orderBy = args.orderBy || CustomViewSortOrder.ASC;
        const hasExplicitLimit = !!(args.limit && args.limit > 0);

        // Was a person requested at all (independently of whether we could resolve them to an email)?
        const personRequested = !!(args.attendees && args.attendees.length > 0);

        // Résolution des participants en adresses email (comportement inchangé)
        let resolvedAttendees: string[] = [];
        if (personRequested) {
            ({ emails: resolvedAttendees } = await this.resolveAttendees(
                args.attendees!,
            ));
        }

        // Filter needles: resolved emails when available, otherwise fall back to the raw requested labels.
        // (searchContact may resolve nothing even though the name appears in the event's attendees — the
        // client-side matcher already matches on attendee.emailAddress.name, so a raw label works.)
        const attendeeNeedles =
            resolvedAttendees.length > 0
                ? resolvedAttendees
                : personRequested
                  ? args.attendees!.map((a) => a.trim()).filter(Boolean)
                  : [];

        // The filter stays active as soon as a person is requested — even UNRESOLVED — so we NEVER fall
        // back to "the whole agenda" when a person filter was explicitly asked for.
        const hasAttendeeFilter = personRequested;
        // Sans participant : on récupère un bloc AVEC body (c'est déjà le bloc affiché).
        // Avec participant : scan léger (sans body) — le body est hydraté ensuite pour le bloc affiché.
        const includeBody = !hasAttendeeFilter;
        const scanPageSize = hasAttendeeFilter
            ? AGENDA_ATTENDEE_SCAN_PAGE
            : AGENDA_FETCH_BLOCK;

        // Le SDK honore la fenêtre [startDate, endDate] émise par l'api-ai (exécuteur pur — AB#158).
        // attendees uses attendeeNeedles (never empty when a person is requested) and is only ever consumed
        // client-side by AgendaManager._matchesAttendees — never injected into the Graph query.
        const baseFilter: AgendaFilter = {
            startDate,
            endDate,
            attendees: attendeeNeedles,
            orderBy,
            keywords: args.keywords,
        };

        // Récupération du premier bloc (cible = N si limite explicite, sinon un bloc)
        const firstTarget = hasExplicitLimit ? args.limit! : AGENDA_FETCH_BLOCK;
        const { items: firstBlock, nextCursor } =
            await this._accumulateAgendaItems(
                baseFilter,
                undefined,
                firstTarget,
                includeBody,
                scanPageSize,
            );

        // Analyse sur l'INTÉGRALITÉ de la fenêtre (fire-and-forget — ne bloque jamais l'affichage).
        // `analysisRequest` absent : pas d'analyse (F-0.8).
        const hasUpcomingAnalysis =
            !!args.analysisRequest?.enabled && firstBlock.length > 0;
        if (hasUpcomingAnalysis) {
            this._runAgendaAnalysis(baseFilter, args, userRequestId);
        }

        // Contexte de pagination partagé (limite explicite ET fenêtre ouverte).
        const paginationCtx: AgendaPaginationContext = {
            reqId: userRequestId,
            baseFilter,
            includeBody,
            scanPageSize,
        };

        // Limite explicite (« mes 30 prochains ») → on borne à N, mais on pagine quand même 6-par-6 DANS
        // ces N. Aucun fetch au-delà de N : on passe un buffer déjà tronqué à N, sans curseur ni préchargement.
        if (hasExplicitLimit) {
            const limited = firstBlock.slice(0, args.limit!);
            const initialItems = limited.slice(0, AGENDA_DISPLAY_BATCH);
            await this._hydrateDisplayedItems(initialItems, includeBody);
            // Offset = ce qui a réellement été affiché (initialItems.length), PAS AGENDA_DISPLAY_BATCH : si
            // moins de 6 items sont disponibles, « charger plus » doit reprendre à initialItems.length.
            const moreDataCallBack = this._buildAgendaMoreCallback(
                paginationCtx,
                limited,
                initialItems.length,
                undefined,
                undefined,
            );
            return this._buildAgendaComponentData(
                userRequestId,
                args.aiComment,
                initialItems,
                moreDataCallBack,
                hasUpcomingAnalysis,
            );
        }

        // Sans limite → on affiche AGENDA_DISPLAY_BATCH et on pagine via « charger plus » (fenêtre ouverte).
        const initialItems = firstBlock.slice(0, AGENDA_DISPLAY_BATCH);
        // Hydrate uniquement la tranche affichée (pas tout le bloc accumulé — cf. plafond $batch).
        await this._hydrateDisplayedItems(initialItems, includeBody);
        // Précharge le bloc suivant dès maintenant, en arrière-plan → 1ᵉʳ « charger plus » transparent.
        const initialPrefetch = nextCursor
            ? this._loadNextAgendaBlock(paginationCtx, nextCursor)
            : undefined;
        // Offset = initialItems.length (et NON AGENDA_DISPLAY_BATCH) : quand le 1ᵉʳ bloc accumulé contient
        // < 6 items avec un curseur restant (chemin participant très sélectif atteignant le plafond de
        // pages), figer l'offset à 6 sauterait les items d'indices [firstBlock.length, 6) du bloc rechargé.
        const moreDataCallBack = this._buildAgendaMoreCallback(
            paginationCtx,
            firstBlock,
            initialItems.length,
            nextCursor,
            initialPrefetch,
        );

        return this._buildAgendaComponentData(
            userRequestId,
            args.aiComment,
            initialItems,
            moreDataCallBack,
            hasUpcomingAnalysis,
        );
    }

    /**
     * Accumule des AgendaItems (déjà filtrés participants + mappés) en suivant le curseur, jusqu'à
     * atteindre `targetCount` correspondances OU épuiser la fenêtre OU le plafond de pages. Nécessaire
     * car le filtre participant (côté client) réduit chaque page — il faut scanner plusieurs pages
     * serveur pour réunir un bloc. Retourne les items collectés + le curseur pour continuer.
     */
    private async _accumulateAgendaItems(
        baseFilter: AgendaFilter,
        startCursor: string | undefined,
        targetCount: number,
        includeBody: boolean,
        scanPageSize: number,
    ): Promise<{ items: AgendaItem[]; nextCursor?: string }> {
        const collected: AgendaItem[] = [];
        let cursor = startCursor;
        let pages = 0;

        // Le $top n'est utilisé qu'à la 1ʳᵉ requête ; les pages suivantes réutilisent le curseur (nextLink).
        const filter: AgendaFilter = { ...baseFilter, limit: scanPageSize };

        do {
            const page = await this.agendaManager.getUserAgendaPage(
                filter,
                cursor,
                { includeBody },
            );
            collected.push(...page.items);
            cursor = page.nextCursor;
            pages++;

            if (pages >= AGENDA_MAX_SCAN_PAGES) {
                if (cursor) {
                    logger.warn(
                        LOGS.ConversationManager,
                        `_accumulateAgendaItems: plafond de ${AGENDA_MAX_SCAN_PAGES} pages atteint avec curseur restant — scan interrompu (résultat potentiellement partiel)`,
                    );
                }
                break;
            }
        } while (cursor && collected.length < targetCount);

        return { items: collected, nextCursor: cursor };
    }

    /**
     * Hydrate le body **uniquement** pour les items réellement affichés (chemin scan léger, sans body).
     * Borné à la tranche d'affichage (AGENDA_DISPLAY_BATCH) → toujours bien en dessous du plafond `$batch`,
     * et zéro sur-fetch (on n'hydrate jamais les items bufferisés non encore montrés). No-op sans participant
     * (le body est déjà présent) ou sur une tranche vide.
     */
    private async _hydrateDisplayedItems(
        items: AgendaItem[],
        includeBody: boolean,
    ): Promise<void> {
        if (!includeBody && items.length > 0) {
            await this.agendaManager.hydrateBodies(items);
        }
    }

    /**
     * Récupère le bloc agenda suivant (léger, sans hydratation — le body est hydraté par tranche affichée
     * via _hydrateDisplayedItems). Ne rejette JAMAIS : renvoie `null` en cas d'échec — le consommateur
     * retombe alors sur un fetch synchrone ou s'arrête proprement. Utilisé aussi bien en récupération
     * synchrone qu'en préchargement d'arrière-plan.
     */
    private _loadNextAgendaBlock(
        ctx: AgendaPaginationContext,
        cursor: string,
    ): Promise<{ items: AgendaItem[]; nextCursor?: string } | null> {
        return (async () => {
            try {
                const { items, nextCursor } = await this._accumulateAgendaItems(
                    ctx.baseFilter,
                    cursor,
                    AGENDA_FETCH_BLOCK,
                    ctx.includeBody,
                    ctx.scanPageSize,
                );
                return { items, nextCursor };
            } catch (error) {
                logger.error(
                    LOGS.ConversationManager,
                    `_loadNextAgendaBlock error: ${error}`,
                );
                return null;
            }
        })();
    }

    /**
     * Construit le `moreDataCallBack` auto-chaînant de l'agenda. Sert AGENDA_DISPLAY_BATCH items depuis
     * le buffer en mémoire. Le bloc suivant est **préchargé en arrière-plan** (un bloc d'avance, dès qu'un
     * curseur existe) : quand le buffer est vidé, on consomme ce préchargement (déjà en vol / résolu) au
     * lieu de déclencher un fetch bloquant → « charger plus » transparent. Repli sur un fetch synchrone si
     * le préchargement a échoué. Retourne `undefined` quand il n'y a plus rien à servir ni à récupérer.
     */
    private _buildAgendaMoreCallback(
        ctx: AgendaPaginationContext,
        buffer: AgendaItem[],
        offset: number,
        cursor: string | undefined,
        prefetch:
            | Promise<{ items: AgendaItem[]; nextCursor?: string } | null>
            | undefined,
    ): (() => Promise<MoreDataCallbackResult>) | undefined {
        // Plus rien en buffer, plus de curseur et aucun préchargement en cours → fin.
        if (offset >= buffer.length && !cursor && !prefetch) return undefined;

        return async (): Promise<MoreDataCallbackResult> => {
            let localBuffer = buffer;
            let localCursor = cursor;
            let localPrefetch = prefetch;

            // Buffer épuisé → consommer le bloc préchargé (transparent) ; repli fetch synchrone sinon.
            if (
                offset >= localBuffer.length &&
                (localPrefetch || localCursor)
            ) {
                let block = localPrefetch ? await localPrefetch : null;
                if (!block && localCursor) {
                    block = await this._loadNextAgendaBlock(ctx, localCursor);
                }
                if (block) {
                    localBuffer = localBuffer.concat(block.items);
                    localCursor = block.nextCursor;
                } else {
                    localCursor = undefined; // échec définitif → on arrête proprement
                }
                localPrefetch = undefined;
            }

            const slice = localBuffer.slice(
                offset,
                offset + AGENDA_DISPLAY_BATCH,
            );
            const newOffset = offset + slice.length;

            // Hydrate le body de la seule tranche servie (borné à AGENDA_DISPLAY_BATCH) avant de la renvoyer
            // → body présent au rendu, sans sur-fetch ni dépassement du plafond $batch.
            await this._hydrateDisplayedItems(slice, ctx.includeBody);

            // Précharge le bloc suivant en arrière-plan (un seul bloc d'avance) → prochain clic transparent.
            let nextPrefetch = localPrefetch;
            if (!nextPrefetch && localCursor) {
                nextPrefetch = this._loadNextAgendaBlock(ctx, localCursor);
            }

            return {
                userRequestId: ctx.reqId,
                content: slice,
                // Rien de nouveau servi → on arrête (évite une boucle « charger plus » vide).
                moreDataCallBack:
                    slice.length > 0
                        ? this._buildAgendaMoreCallback(
                              ctx,
                              localBuffer,
                              newOffset,
                              localCursor,
                              nextPrefetch,
                          )
                        : undefined,
            };
        };
    }

    /**
     * Assemble le ComponentData d'une réponse agenda (contentType AgendaItems). Pour `getAgendaItems`,
     * avec ou sans limite explicite.
     */
    private _buildAgendaComponentData(
        userRequestId: string,
        aiComment: string | undefined,
        items: AgendaItem[],
        moreDataCallBack: (() => Promise<MoreDataCallbackResult>) | undefined,
        hasUpcomingAnalysis: boolean,
    ): ComponentData {
        const fallbackComment = buildCountMessage(
            items.length,
            AGENDA_ITEMS_COMMENT,
        );
        const componentData: ComponentData = {
            aiComment:
                items.length === 0
                    ? fallbackComment
                    : aiComment || fallbackComment,
            userRequestId,
            content: {
                contentData: items,
                contentType: ComponentDataContentType.AgendaItems,
                contentAction: ContentAction.VIEW,
                upcomingAnalysis: hasUpcomingAnalysis,
                moreDataCallBack,
            },
            aiVocalText: buildCountMessage(items.length, AGENDA_ITEMS_VOCAL),
        };

        return componentData;
    }

    /**
     * Analyse fire-and-forget bornée aux `AGENDA_ANALYSIS_MAX_ITEMS` premiers événements de la fenêtre
     * (elle-même déjà bornée par l'api-ai, ≤ +1 an). Scanne en léger (le minify utilise bodyPreview,
     * pas body), puis invoque l'agent d'analyse. Ne bloque jamais l'affichage.
     *
     * Contrat d'événement (AB#158) : l'appelant a positionné `upcomingAnalysis: true` dans le
     * ComponentData → l'UI attend UN événement terminal. On garantit donc qu'un
     * `analysis-agent-success` OU `analysis-agent-error` est TOUJOURS émis pour ce `userRequestId`, y
     * compris quand le re-fetch revient vide ou échoue — sinon l'écran reste bloqué en « analyse en
     * cours » indéfiniment.
     */
    private _runAgendaAnalysis(
        baseFilter: AgendaFilter,
        args: GetAgendaItemsParams,
        userRequestId: string,
    ): void {
        void (async () => {
            try {
                // Borne le scan ET le payload : cible = page = AGENDA_ANALYSIS_MAX_ITEMS (cap coût/tokens).
                const { items, nextCursor } = await this._accumulateAgendaItems(
                    baseFilter,
                    undefined,
                    AGENDA_ANALYSIS_MAX_ITEMS,
                    false,
                    AGENDA_ANALYSIS_MAX_ITEMS,
                );
                if (nextCursor) {
                    logger.warn(
                        LOGS.ConversationManager,
                        `_runAgendaAnalysis: analyse bornée aux ${AGENDA_ANALYSIS_MAX_ITEMS} premiers événements (cap coût/tokens AB#158) — la fenêtre en contient davantage`,
                    );
                }

                if (items.length === 0) {
                    // Rien à analyser (fenêtre vide au re-fetch — anormal car le bloc affiché était non vide :
                    // échec transitoire / provider Google renvoyant []). On DOIT tout de même émettre un
                    // événement terminal, sinon l'UI reste bloquée sur `upcomingAnalysis: true`.
                    logger.warn(
                        LOGS.ConversationManager,
                        `_runAgendaAnalysis: aucun événement au re-fetch — émission d'un événement terminal (pas d'analyse à produire)`,
                    );
                    this.eventBus.emit("analysis-agent-error", {
                        userRequestId,
                        error: "No agenda items to analyze",
                    });
                    return;
                }

                const userHistory: any[] = this.getSingleMessageCache();
                // Plafonne le minify au même cap que le scan (défense en profondeur si une page dépasse N).
                const minifiedAgendaItems: MinifiedAgenda[] =
                    this.minifyAgendaItems(items, AGENDA_ANALYSIS_MAX_ITEMS);

                const agentInvokeParams: AgentInvokeParams = {
                    userHistory,
                    // Sans type, l'analyse est `custom` : un type vide la faisait échouer (F-0.8).
                    type: args.analysisRequest?.type || "custom",
                    instructions: args.analysisRequest?.instruction ?? "",
                    contentData: minifiedAgendaItems,
                    sessionId: userRequestId,
                    availableTools: analysisAgentTools,
                };

                // invokeAnalysisAgent émet lui-même success/error à la résolution → contrat terminal tenu.
                this.invokeAnalysisAgent(agentInvokeParams);
            } catch (error) {
                logger.error(
                    LOGS.ConversationManager,
                    `_runAgendaAnalysis error: ${error}`,
                );
                // Échec du scan : émettre l'événement terminal d'erreur, sinon l'UI attend une analyse qui
                // n'arrivera jamais (upcomingAnalysis: true resté sans réponse).
                this.eventBus.emit("analysis-agent-error", {
                    userRequestId,
                    error:
                        error instanceof Error
                            ? error.message
                            : "Agenda analysis failed",
                });
            }
        })();
    }

    private async executeCreateAgendaItemImplementation(
        args: CreateAgendaItemParams,
    ): Promise<ComponentData> {
        // Create user request id
        const userRequestId: string = generateUUID();

        // Dates (F-1.9) : date illisible → message, sans brouillon ; fin avant le début → remises dans
        // l'ordre, et écrites dans les logs.
        const period = readAgendaPeriod(args.start, args.end);
        if (!period) {
            logger.warn(
                LOGS.ConversationManager,
                `executeCreateAgendaItemImplementation() - date illisible : message`,
            );
            return buildPeriodMessageResponse(UNREADABLE_EVENT_DATE_MESSAGE);
        }
        if (period.isInverted) {
            logger.warn(
                LOGS.ConversationManager,
                `executeCreateAgendaItemImplementation() - start > end détecté, inversion automatique`,
            );
        }
        const { start, end } = period;

        let identifiedUsers: string[] = [];
        // F-1.13 : participants introuvables, dits à l'utilisateur au lieu de disparaître.
        let unresolvedAttendees: string[] = [];
        let unavailableAttendees: string[] = [];

        // Ajout des adresses email des destinataires si fournies
        if (args.attendees && args.attendees.length > 0) {
            const { emails, unresolved, unavailable } =
                await this.resolveAttendees(args.attendees);
            // Fusionner sans doublon.
            identifiedUsers = [...new Set(emails)];
            unresolvedAttendees = unresolved;
            unavailableAttendees = unavailable;
        }

        // Construction de l'objet CreateAgendaItemData avec les données fournies
        const agendaItemData: CreateAgendaItemData = {
            subject: args.subject,
            start: start,
            end: end,
            description: args.description,
            location: args.location,
            attendees: identifiedUsers,
            isAllDay: args.isAllDay ?? false,
        };

        // Formation de la réponse avec les données de l'événement à créer
        const unresolvedMessage = [
            unresolvedAttendeesMessage(unresolvedAttendees),
            unavailableAttendeesMessage(unavailableAttendees),
        ]
            .filter(Boolean)
            .join(" ");
        const componentData: ComponentData = {
            aiComment: unresolvedMessage
                ? `${args.aiComment} ${unresolvedMessage}`.trim()
                : args.aiComment,
            userRequestId: userRequestId,
            content: {
                contentData: agendaItemData,
                contentType: ComponentDataContentType.CreateAgendaItem,
                contentAction: ContentAction.VIEW,
                upcomingAnalysis: false,
            },
            aiVocalText: `J'ai préparé l'événement "${args.subject}" pour vous. Vous pouvez maintenant confirmer sa création.`,
        };
        return componentData;
    }

    /**
     * Début d'un tour, pour les contacts : si le message est la réponse cliquée à la question posée au
     * tour précédent (« Pour « jessica » : Jessica Bay <…> »), le contact choisi vaut pour le reste de
     * la conversation. Les résolutions du tour précédent sont oubliées.
     */
    private startContactTurn(message: string): void {
        const pending = this.pendingContactQuestion;
        const email = pending?.emailsByReply.get(message.trim());
        if (pending && email) {
            // Le choix est un fait de la conversation, seul endroit où il est gardé : il survit au
            // compactage et à la réouverture, se voit et s'oublie à l'écran comme les autres faits.
            this.setFacts(noteContact(this.facts, pending.label, email));
        }
        this.pendingContactQuestion = undefined;
        this.askedContactQuestions = [];
        for (const key of this.unavailableContacts) {
            this.contactResolutions.delete(key);
        }
        this.unavailableContacts.clear();
    }

    /**
     * Le rendez-vous en attente si le message est le clic sur un choix de sa question ; tout autre
     * message l'abandonne (il part à l'assistant, qui repropose avec ce que l'utilisateur a écrit).
     */
    private takePendingAgenda(message: string): PendingAgenda | undefined {
        const pending = this.pendingAgenda;
        this.pendingAgenda = undefined;
        return pending?.question.emailsByReply.has(message.trim())
            ? pending
            : undefined;
    }

    /**
     * Le rendez-vous en attente, terminé par l'app (R3) : la même fin de tour, reconstruite maintenant
     * que le contact choisi est connu, sans appel à l'Api-Ai ; la carte porte le message de l'assistant.
     * Un autre participant reste à préciser : sa question, et le rendez-vous attend encore. Le contact
     * choisi part à l'historique avec les faits (sans Api-Ai, aucune réponse ne les y enverrait).
     */
    private async finishPendingAgenda(
        pending: PendingAgenda,
        toolImplementations: ToolImplementation,
    ): Promise<ComponentData> {
        const response = await this.buildEtapesAnswerResponse(
            pending.reading,
            pending.reads,
            toolImplementations,
        );
        const turn = {
            ...(pending.turnId ? { turnId: pending.turnId } : {}),
            ...(pending.answerModel
                ? { answerModel: pending.answerModel }
                : {}),
        };
        if (this.pendingAgenda?.reading === pending.reading) {
            this.pendingAgenda = { ...this.pendingAgenda, ...turn };
        }
        this.persistFacts();
        return { ...response, ...turn };
    }

    /**
     * Le contact d'un nom : celui que l'utilisateur a déjà choisi pendant la conversation, sinon le plus
     * pertinent (`ContactManager.resolveBestContact`, F-1.13). Un nom n'est cherché qu'une fois par
     * conversation (sauf s'il n'a pas pu l'être). Une personne identifiée l'est quel que soit le nom
     * employé (R1) : parmi les contacts à choisir pour ce nom, une seule adresse déjà choisie, c'est elle.
     * Le contrat v1 garde le délai RAG de 2 s qu'il avait (CONCEPTION-contacts, D5) ; le contrat par
     * étapes attend le RAG plus longtemps.
     */
    private resolveContact(name: string): Promise<BestContactResult> {
        const key = name.trim().toLowerCase();
        const chosen = contactOf(this.facts, name);
        if (chosen) {
            return Promise.resolve({
                emails: [chosen],
                source: "rag",
                candidates: [],
                ambiguous: false,
            });
        }
        let resolution = this.contactResolutions.get(key);
        if (!resolution) {
            resolution =
                this.contactManager.resolveBestContact(
                    name,
                    this.conversationContract === "etapes"
                        ? undefined
                        : { timeoutMs: RAG_DEFAULT_TIMEOUT_MS },
                ) ??
                Promise.resolve<BestContactResult>({
                    emails: [],
                    source: "legacy",
                    candidates: [],
                    ambiguous: false,
                });
            this.contactResolutions.set(key, resolution);
            void resolution.then((result) => {
                if (
                    result.unavailable &&
                    this.contactResolutions.get(key) === resolution
                ) {
                    this.unavailableContacts.add(key);
                }
            });
        }
        // Les choix lus au moment où la recherche répond : un contact choisi entre-temps compte.
        return resolution.then((result) =>
            withChosenContact(result, chosenContacts(this.facts)),
        );
    }

    /**
     * La question à poser avant de préparer un rendez-vous : le premier participant dont le nom désigne
     * plusieurs contacts, ou est introuvable mais ressemble à un contact. Aucun : `undefined`.
     */
    private async attendeesQuestion(
        names: string[],
    ): Promise<ContactQuestion | undefined> {
        // Toutes les recherches partent ensemble (chacune peut attendre le RAG puis le carnet) ; la
        // question porte sur le premier nom, dans l'ordre demandé.
        const searched = names
            .filter((name) => name.trim() !== "" && !name.includes("@"))
            .map((name) => ({ name, resolution: this.resolveContact(name) }));
        for (const { name, resolution } of searched) {
            const question = contactQuestion(name.trim(), await resolution);
            if (question) return question;
        }
        return undefined;
    }

    /**
     * F-1.13 : UN contact par nom (le plus pertinent dont le nom correspond), pas tous les voisins
     * rendus par la recherche de contacts. `emails` : les adresses trouvées, dans l'ordre des noms,
     * sans les no-reply / yammer (jamais un participant d'agenda, quelle que soit la source).
     * `unresolved` : les noms sans contact, dans l'ordre où leur recherche s'est terminée.
     */
    private async resolveAttendees(names: string[]): Promise<{
        emails: string[];
        unresolved: string[];
        unavailable: string[];
    }> {
        const unresolved: string[] = [];
        const unavailable: string[] = [];
        const found = await Promise.all(
            names.map(async (name) => {
                const { emails, unavailable: notSearched } =
                    await this.resolveContact(name);
                if (emails.length === 0 && name.trim() !== "") {
                    (notSearched ? unavailable : unresolved).push(name.trim());
                }
                return emails;
            }),
        );
        return {
            emails: found
                .flat()
                .filter((contact) => !isNoReplyAddress(contact)),
            unresolved,
            unavailable,
        };
    }

    private executeSuggestToolsImplementation(
        args: SuggestAvailableToolsParams,
    ): ComponentData {
        // Create user request id
        const userRequestId: string = generateUUID();

        type ToolName =
            | "searchMails"
            | "basicAiChat"
            | "createAgendaItem"
            | "getAgendaItems"
            | "findFreeTimeSlots";

        // Fonction pour obtenir une description aléatoire d'un outil
        const getRandomToolDescription = (toolName: string): string => {
            if (!(toolName in toolDescriptions)) return "";
            const descriptions = toolDescriptions[toolName as ToolName];
            if (!descriptions || descriptions.length === 0) return "";
            const randomIndex = Math.floor(Math.random() * descriptions.length);
            return descriptions[randomIndex];
        };

        // Fonction pour obtenir le titre formaté d'un outil
        const getToolTitle = (toolName: string): string => {
            const titles: Record<ToolName, string> = {
                searchMails: "📧 Recherche d'emails",
                basicAiChat: "💬 Conseils et discussion",
                createAgendaItem: "📅 Création d'événement",
                getAgendaItems: "📋 Consultation d'agenda",
                findFreeTimeSlots: "🕐 Recherche de créneaux libres",
            };
            return titles[toolName as ToolName] || toolName;
        };

        // Construction du message de suggestions
        const suggestionsList = args.availableToolNames
            .map((toolName) => {
                const title = getToolTitle(toolName);
                const description = getRandomToolDescription(toolName);
                return `${title}\n${description}`;
            })
            .join("\n    \n");

        const aiSuggestion = `Voici ce que je peux faire pour vous :\n\n${suggestionsList}`;

        // Création de la réponse avec le même format que fallbackResponse
        return buildTextResponse(args.aiComment, {
            suggestion: aiSuggestion,
            userRequestId,
        });
    }

    private async executeSearchMailsImplementation(
        args: SearchMailsParams,
    ): Promise<ComponentData> {
        const userAction = this.stringToContentActionFromKey(args.userAction);
        const isGroupAction =
            userAction !== undefined && userAction !== ContentAction.VIEW;

        // Dates (F-1.9) : seule une date ISO 8601 lisible entre dans le filtre. Début après la fin :
        // message. Date illisible : refus pour une action groupée — retirer la date l'élargirait — ;
        // en consultation, la période est ignorée et on le dit.
        const period = readSearchPeriod(args.startDate, args.endDate);
        if (period.status === "inverted") {
            logger.warn(
                LOGS.ConversationManager,
                `searchMails - période qui finit avant de commencer : message`,
            );
            return buildPeriodMessageResponse(INVERTED_PERIOD_MESSAGE);
        }
        if (period.status === "unreadable" && isGroupAction) {
            logger.warn(
                LOGS.ConversationManager,
                `searchMails - date illisible pour une action groupée : refus`,
            );
            return buildPeriodMessageResponse(
                UNREADABLE_PERIOD_FOR_ACTION_MESSAGE,
            );
        }
        const isPeriodIgnored = period.status === "unreadable";
        if (isPeriodIgnored) {
            logger.warn(
                LOGS.ConversationManager,
                `searchMails - date illisible : période ignorée en consultation`,
            );
        }

        const { filter, unresolvedNames } = await this.buildSearchFilter(
            args,
            period,
        );

        // G1 (F-1.1) : pas de suppression sans critère — ni recherche, ni bloc [ref]. Les autres actions
        // sans critère gardent le comportement actuel (CAS 4) jusqu'à la décision « tous mes mails ».
        if (userAction === ContentAction.DELETE && !hasCriteria(filter)) {
            logger.warn(
                LOGS.ConversationManager,
                `searchMails - suppression sans critère refusée (G1)`,
            );
            return buildTextResponse(DELETE_WITHOUT_CRITERIA_MESSAGE);
        }

        const limit = args.limit && args.limit > 0 ? args.limit : 25;
        const hasExplicitLimit = args.limit ? true : false;

        let searchResults = await this.mailManager.getViewFilterMails(
            filter,
            limit,
        );
        // Filet (F-1.2) : jamais plus que la limite, quel que soit le fournisseur.
        searchResults = searchResults.slice(0, limit);

        // G2 (F-1.3) : au-delà du seuil de masse, pas d'action groupée — la liste reste en consultation.
        const { isMassAction, contentAction, massComment } = groupActionGuard(
            searchResults.length,
            userAction,
        );

        // Aucun mail et des noms introuvables dans les contacts : on les cite (F-1.14).
        const unknownContactsComment =
            searchResults.length === 0 && unresolvedNames.size > 0
                ? noMailForUnknownContactsMessage([...unresolvedNames])
                : undefined;
        const fallbackComment =
            unknownContactsComment ??
            buildCountMessage(searchResults.length, MAIL_SEARCH_COMMENT);
        // Période ignorée (F-1.9) : le texte de l'Api-Ai la citerait à tort ; on donne le nombre de mails
        // trouvés, et on dit que la période n'a pas été comprise.
        const aiComment =
            massComment ??
            (isPeriodIgnored
                ? `${fallbackComment} ${PERIOD_IGNORED_MESSAGE}`
                : searchResults.length === 0
                  ? fallbackComment
                  : args.aiComment || fallbackComment);

        // Create user request id
        const userRequestId: string = generateUUID();

        // Mémorisation du result set pour référence ultérieure (applyActionToPreviousResults).
        // Uniquement si la recherche a retourné des résultats — pas de bloc [ref:Rx] sinon.
        if (searchResults.length > 0) {
            // L'historique décrit la recherche faite : sans la période ignorée (F-1.9).
            this.rememberResultSet(
                userRequestId,
                searchResults,
                filter,
                isPeriodIgnored
                    ? { ...args, startDate: undefined, endDate: undefined }
                    : args,
            );
        }

        let hasUpcomingAnalysis: boolean = false;

        // `analysisRequest` absent : pas d'analyse (F-0.8).
        if (args.analysisRequest?.enabled && searchResults.length !== 0) {
            hasUpcomingAnalysis = true;
            this.launchMailsAnalysis(
                searchResults,
                args.analysisRequest,
                userRequestId,
            );
        }

        // Texte lu (F-1.8) : une action groupée est proposée, à confirmer à l'écran — jamais
        // « je supprime ». Le texte affiché et l'historique gardent l'aiComment.
        let aiVocalText: string;
        if (isMassAction) {
            aiVocalText = aiComment;
        } else if (unknownContactsComment) {
            aiVocalText = unknownContactsComment;
        } else if (isGroupAction) {
            aiVocalText = buildCountMessage(
                searchResults.length,
                ACTION_PROPOSAL_VOCAL[userAction],
            );
        } else {
            aiVocalText = buildCountMessage(
                searchResults.length,
                MAIL_SEARCH_VOCAL,
            );
        }
        if (isPeriodIgnored) {
            aiVocalText = `${aiVocalText} ${PERIOD_IGNORED_MESSAGE}`;
        }

        // Formation de la réponse avec les résultats
        const componentData: ComponentData = {
            aiComment: aiComment,
            userRequestId: userRequestId,
            content: {
                contentData: searchResults,
                contentType: ComponentDataContentType.Mails,
                contentAction,
                upcomingAnalysis: hasUpcomingAnalysis,
                // « Voir plus » seulement en consultation (F-1.3) : une action groupée porte sur la liste
                // affichée, que « voir plus » agrandirait.
                moreDataCallBack:
                    isGroupAction ||
                    hasExplicitLimit ||
                    (!hasExplicitLimit && searchResults.length !== limit)
                        ? undefined
                        : () =>
                              this.executeCompleteSearchMailCallBack(
                                  filter,
                                  userRequestId,
                              ),
            },
            aiVocalText: aiVocalText,
        };
        return componentData;
    }

    /**
     * Filtre d'une recherche de mails, à partir des critères de l'Api-Ai (`searchMails`, ou l'étape
     * `lireMails` du contrat par étapes) : mots-clés, statuts, période déjà contrôlée par l'appelant,
     * expéditeurs et destinataires résolus par les contacts, catégories, tri. `unresolvedNames` : les
     * noms (sans « @ ») que les contacts ne résolvent pas (F-1.14).
     */
    private async buildSearchFilter(
        args: SearchMailsParams,
        period: SearchPeriod,
    ): Promise<{
        filter: ViewFilter;
        unresolvedNames: Set<string>;
        people: EtapePersonne[];
        /** Contrat par étapes : la question à poser avant de lire (le premier nom à préciser). */
        question?: ContactQuestion;
    }> {
        // Construction du filtre de recherche avec les critères fournis
        const filter: ViewFilter = {
            keywords: [],
            startDate: null,
            endDate: null,
            identifiedSenders: [],
            identifiedRecipients: [],
            categories: [],
            mailStatus: [],
            sortOrder: CustomViewSortOrder.DESC,
        };

        // Ajout des mots-clés si fournis
        if (args.keywords && args.keywords.length > 0) {
            filter.keywords = args.keywords;
        }

        // Ajout du statut de lecture si fourni
        if (args.readStatus) {
            filter.readStatus = args.readStatus;
        }

        // Ajout du filtre de pièces jointes si fourni
        if (args.hasAttachment !== undefined) {
            filter.hasAttachment = args.hasAttachment;
        }

        if (period.status === "ok") {
            // Configuration de la date de fin si fournie
            if (period.end) {
                filter.endDate = period.end.toISOString();
            }

            // Configuration de la date de début si fournie
            if (period.start) {
                filter.startDate = period.start.toISOString();
            }
        }

        // Préparation des promesses pour les expéditeurs (une recherche RAG-first par terme, ordre préservé)
        const fromTerms = [
            ...new Set((args.from || []).map((s) => s.toLowerCase())),
        ];
        // F-1.13 : un contact par terme (le plus pertinent dont le nom correspond) ; introuvable →
        // terme brut, comme avant (une organisation, « teamway », se cherche ainsi dans les adresses).
        const fromPromises: Promise<ResolveContactsResult>[] = fromTerms.map(
            (userSender) => this.resolveContact(userSender),
        );

        // Préparation des promesses pour les destinataires
        const toTerms = [
            ...new Set((args.to || []).map((s) => s.toLowerCase())),
        ];
        const toPromises: Promise<ResolveContactsResult>[] = toTerms.map(
            (userRecipient) => this.resolveContact(userRecipient),
        );

        // Exécution de toutes les recherches en parallèle
        const [fromResults, toResults] = await Promise.all([
            Promise.allSettled(fromPromises),
            Promise.allSettled(toPromises),
        ]);

        // F-1.14 : noms (sans « @ ») que les contacts ne résolvent pas, cités si la recherche ne trouve rien.
        const unresolvedNames = new Set<string>();
        // Contrat par étapes : comment chaque nom demandé a été compris, rendu à l'Api-Ai avec la lecture.
        const people: EtapePersonne[] = [];
        // Contrat par étapes : le premier nom à faire préciser par l'utilisateur, s'il y en a un.
        let question: ContactQuestion | undefined;

        // AB#1634 / AB#1744 : par terme, on applique l'enveloppe de substitution `[term]` sur le résultat
        // BRUT (avant tout filtre, reproduit `result?.length>0 ? result : [term]`), puis :
        //  - `source === 'rag'`    → PAS de filtre no-reply (la pertinence RAG remplace l'heuristique) ;
        //  - `source === 'legacy'` → filtre no-reply par terme (comportement historique inchangé).
        // `fromResults[i]` / `toResults[i]` s'alignent sur `fromTerms[i]` / `toTerms[i]`.
        // `requested` : les termes tels que l'Api-Ai les a écrits, pour citer un nom introuvable.
        const resolveContactsPerTerm = (
            results: PromiseSettledResult<ResolveContactsResult>[],
            terms: string[],
            requested: string[],
        ): string[] => {
            const resolved: string[] = [];
            results.forEach((result, index) => {
                if (result.status !== "fulfilled") return;
                const { emails, source } = result.value;
                const term = terms[index];
                const name = (
                    requested.find((n) => n.toLowerCase() === term) ?? term
                ).trim();
                if (
                    emails.length === 0 &&
                    term.trim() !== "" &&
                    !term.includes("@")
                ) {
                    unresolvedNames.add(name);
                }
                if (term.trim() !== "" && !term.includes("@")) {
                    const resolution =
                        result.value as Partial<BestContactResult> &
                            ResolveContactsResult;
                    people.push(toEtapePersonne(name, resolution));
                    question ??= contactQuestion(name, resolution);
                }
                // Enveloppe de substitution : sans résolution, on retombe sur le terme brut.
                const base = emails.length > 0 ? emails : [terms[index]];
                const contactsForTerm =
                    source === "rag"
                        ? base
                        : filterNoReplyContactsForQuery(base, terms[index]);
                resolved.push(...contactsForTerm);
            });
            // Suppression des doublons inter-termes avec Set
            return [...new Set(resolved)];
        };

        // Traitement des résultats pour les expéditeurs
        if (args.from && args.from.length > 0) {
            filter.identifiedSenders = resolveContactsPerTerm(
                fromResults,
                fromTerms,
                args.from,
            );
        }

        // Traitement des résultats pour les destinataires
        if (args.to && args.to.length > 0) {
            filter.identifiedRecipients = resolveContactsPerTerm(
                toResults,
                toTerms,
                args.to,
            );
        }

        // Ajout des catégories si fournies
        if (args.categories && args.categories.length > 0) {
            filter.categories = args.categories;
        }

        // Ajout des états des mails si fournies
        if (args.mailStatus && args.mailStatus.length > 0) {
            filter.mailStatus = args.mailStatus;
        }

        // Définition de l'ordre de tri : les plus récents quand l'Api-Ai n'en donne pas (F-1.5)
        filter.sortOrder = args.sortOrder ?? CustomViewSortOrder.DESC;

        return { filter, unresolvedNames, people, question };
    }

    /**
     * Mémorise une liste de mails affichée sous un alias « Rx », pour une action au tour suivant
     * (applyActionToPreviousResults), avec le bloc [ref:Rx …] que l'historique reprend.
     */
    private rememberResultSet(
        userRequestId: string,
        mails: Mail[],
        filter: ViewFilter,
        refArgs: SearchMailsParams,
    ): void {
        const refAlias = `R${++this.refCounter}`;
        this.resultSetCache.set(refAlias, {
            userRequestId: userRequestId,
            // uniqueMailvistaId stampé par _setMailInRecord (chokepoint Record) — fallback via le
            // helper du noyau jumeau mailIdentity (jamais reconstruire le format à la main)
            uniqueMailvistaIds: mails.map(
                (mail) => mail.uniqueMailvistaId ?? uniqueMailvistaId(mail),
            ),
            filter: filter,
            contentType: ComponentDataContentType.Mails,
            refBlock: this.buildResultSetRefBlock(
                refAlias,
                mails.length,
                refArgs,
            ),
        });
    }

    /**
     * Lance l'analyse de mails affichés, sans l'attendre : l'événement `analysis-agent-*` porte le
     * `userRequestId` de la réponse. Contrat par étapes (`refs`, ceux des mails dans l'ordre) : son
     * résultat entre aussi dans la mémoire de la conversation, avec la question suivante.
     */
    private launchMailsAnalysis(
        mails: Mail[],
        analysisRequest: { type?: string; instruction?: string },
        userRequestId: string,
    ): void {
        // Getting user history
        const userHistory: any[] = this.getSingleMessageCache();

        // Minifying Search Result Data
        const minifiedMails: MinifiedMail[] = this.minifyMails(
            mails,
            MAILS_ANALYSIS_MAX,
        );

        // Building Third Agent Invoke Object
        const agentInvokeParams: AgentInvokeParams = {
            userHistory: userHistory,
            // Sans type, l'analyse est `custom` : un type vide la faisait échouer (F-0.8).
            type: analysisRequest.type || "custom",
            instructions: analysisRequest.instruction ?? "",
            contentData: minifiedMails,
            sessionId: userRequestId,
            availableTools: analysisAgentTools,
        };

        // Call to agent not async and event will send the result with the session id
        this.invokeAnalysisAgent(agentInvokeParams);
    }

    /**
     * Applique une action sur un result set précédemment affiché, référencé par son alias "Rx".
     * Ressort les copies EXACTES (uniqueMailvistaIds) mémorisées dans le resultSetCache — AUCUNE
     * recherche relancée, zéro variance avec ce que l'utilisateur a vu à l'écran.
     * Alias inconnu (F-1.6) : DELETE refusé, même avec une seule liste en cache (on ne devine jamais
     * la cible d'une action destructive) ; autres actions → la dernière liste affichée, et on le dit.
     * Cache vide, 0 mail retrouvé ou action inconnue de cette version → réponse texte, sans bouton.
     */
    private async executeApplyActionToPreviousResultsImplementation(
        args: ApplyActionToPreviousResultsParams,
        userRequestId: string,
    ): Promise<ComponentData> {
        logger.info(
            LOGS.ConversationManager,
            `executeApplyActionToPreviousResultsImplementation(targetRef: ${args.targetRef}, userAction: ${args.userAction})`,
        );

        // 0. Action inconnue de cette version (contrat v1 tolérant, F-1.6) : réponse texte, sans bouton.
        const userAction = this.stringToContentActionFromKey(args.userAction);
        if (userAction === undefined) {
            logger.warn(
                LOGS.ConversationManager,
                `applyActionToPreviousResults - action inconnue de cette version : réponse texte`,
            );
            return { ...buildUnsupportedResponse(), userRequestId };
        }

        // 1. Lookup direct par alias
        let entry = this.resultSetCache.get(args.targetRef);
        let isLastListFallback = false;

        // 2. Alias inconnu (F-1.6) : on ne devine jamais la cible d'un DELETE, même avec une seule
        //    liste en cache ; les autres actions portent sur la dernière liste affichée, et on le dit.
        if (!entry && this.resultSetCache.size > 0) {
            if (userAction === ContentAction.DELETE) {
                logger.warn(
                    LOGS.ConversationManager,
                    `applyActionToPreviousResults - alias inconnu "${args.targetRef}" avec action DELETE : refus de deviner la cible`,
                );
                const deleteComment = `Je ne suis pas certain des emails que vous souhaitez supprimer.`;
                const deleteSuggestion = `Pourriez-vous préciser quels emails sont concernés, par exemple en relançant la recherche correspondante ?`;
                return buildTextResponse(deleteComment, {
                    suggestion: deleteSuggestion,
                    userRequestId,
                });
            }
            logger.warn(
                LOGS.ConversationManager,
                `applyActionToPreviousResults - alias inconnu "${args.targetRef}" : utilisation de la dernière liste affichée`,
            );
            // La Map est ordonnée par insertion → la dernière entrée est la plus récente (déterministe, sans tri)
            entry = [...this.resultSetCache.values()].at(-1);
            isLastListFallback = true;
        }

        // 3. Cache vide (reset, nouvelle session) → inviter à relancer la recherche
        if (!entry) {
            logger.warn(
                LOGS.ConversationManager,
                `applyActionToPreviousResults - resultSetCache vide, impossible de résoudre "${args.targetRef}"`,
            );
            const fallbackComment = `Je ne retrouve plus les résultats auxquels vous faites référence.`;
            const fallbackSuggestion = `Pourriez-vous relancer votre recherche afin que je puisse appliquer l'action souhaitée ?`;
            return buildTextResponse(fallbackComment, {
                suggestion: fallbackSuggestion,
                userRequestId,
            });
        }

        // 4. Récupération des copies EXACTES depuis le Record (zéro recherche, zéro fetch) :
        //    les mails du result set sont déjà dans le Record (ils en proviennent via getViewFilterMails)
        const mails: Mail[] = [];
        for (const uniqueMailvistaId of entry.uniqueMailvistaIds) {
            const mail =
                this.mailManager.getMailFromRecordByUniqueMailvistaId(
                    uniqueMailvistaId,
                );
            if (mail) {
                mails.push(mail);
            }
        }

        // 5. Aucun mail retrouvé (tous supprimés ou déplacés entre-temps) : réponse texte, sans bouton (F-1.6).
        if (mails.length === 0) {
            logger.warn(
                LOGS.ConversationManager,
                `applyActionToPreviousResults - aucun mail du result set retrouvé (supprimés ou déplacés entre-temps ?)`,
            );
            const goneComment = `Je ne retrouve plus les mails de cette liste : ils ont peut-être été supprimés ou déplacés entre-temps.`;
            const goneSuggestion = `Pourriez-vous relancer votre recherche afin que je puisse appliquer l'action souhaitée ?`;
            return buildTextResponse(goneComment, {
                suggestion: goneSuggestion,
                userRequestId,
            });
        }

        // Fallback si l'IA n'a pas fourni d'aiComment (évite "undefined (...)" et aiVocalText vide)
        let aiComment = args.aiComment || `J'applique l'action sur vos emails.`;
        // Alias inconnu : la liste visée est la dernière affichée, on le dit (F-1.6).
        if (isLastListFallback) {
            aiComment = `${aiComment} (sur la dernière liste affichée)`;
        }
        // Mails supprimés/déplacés entre-temps : on applique sur les copies encore valides et on mentionne l'écart
        if (mails.length < entry.uniqueMailvistaIds.length) {
            logger.warn(
                LOGS.ConversationManager,
                `applyActionToPreviousResults - ${entry.uniqueMailvistaIds.length - mails.length} mail(s) du result set introuvable(s) (supprimés ou déplacés entre-temps ?)`,
            );
            aiComment = `${aiComment} (${mails.length} des ${entry.uniqueMailvistaIds.length} emails d'origine retrouvés)`;
        }

        // G2 (F-1.3) : même seuil que pour une recherche — utile après « voir plus », qui charge tout.
        const isGroupAction = userAction !== ContentAction.VIEW;
        const { isMassAction, contentAction, massComment } = groupActionGuard(
            mails.length,
            userAction,
        );
        if (massComment !== undefined) {
            aiComment = massComment;
        }

        // Formation de la réponse : mêmes mails ré-affichés, avec la nouvelle action utilisateur
        const componentData: ComponentData = {
            aiComment: aiComment,
            userRequestId: userRequestId,
            content: {
                contentData: mails,
                contentType: ComponentDataContentType.Mails,
                contentAction,
                upcomingAnalysis: false,
            },
            // Texte lu (F-1.8) : une action groupée est proposée, à confirmer à l'écran — jamais
            // « je supprime ». Le texte affiché et l'historique gardent l'aiComment.
            aiVocalText:
                isGroupAction && !isMassAction
                    ? buildCountMessage(
                          mails.length,
                          ACTION_PROPOSAL_VOCAL[userAction],
                      )
                    : aiComment,
        };
        return componentData;
    }

    /**
     * G4 (F-1.1) : retire d'une liste proposée en DELETE les mails déjà dans la corbeille — une
     * suppression les y purgerait pour de bon — et le dit. Liste vide après exclusion : réponse texte,
     * sans bouton. Corbeille pas encore connue : pas de suppression, la liste reste en consultation.
     * Les autres réponses passent telles quelles.
     */
    private applyTrashGuard(response: ComponentData): ComponentData {
        const { content } = response;
        if (
            content.contentType !== ComponentDataContentType.Mails ||
            content.contentAction !== ContentAction.DELETE ||
            !Array.isArray(content.contentData)
        ) {
            return response;
        }

        const trashFolderId = this.mailManager.getDeletedItemsFolderId();
        if (!trashFolderId) {
            logger.warn(
                LOGS.ConversationManager,
                `applyTrashGuard - dossier corbeille inconnu : suppression passée en consultation (G4)`,
            );
            return {
                ...response,
                aiComment: TRASH_UNKNOWN_MESSAGE,
                content: { ...content, contentAction: ContentAction.VIEW },
                aiVocalText: TRASH_UNKNOWN_MESSAGE,
            };
        }

        const { kept, excludedCount } = excludeTrashForDelete(
            content.contentData as Mail[],
            trashFolderId,
        );
        if (excludedCount === 0) {
            return response;
        }

        logger.info(
            LOGS.ConversationManager,
            `applyTrashGuard - ${excludedCount} mail(s) déjà dans la corbeille retiré(s) de la suppression (G4)`,
        );

        if (kept.length === 0) {
            const message = allInTrashMessage(excludedCount);
            return {
                ...response,
                aiComment: message,
                content: {
                    ...content,
                    contentData: [],
                    contentAction: ContentAction.VIEW,
                },
                aiVocalText: message,
            };
        }

        const note = trashExcludedMessage(excludedCount);
        const withNote = (text: string): string =>
            text ? `${text} ${note}` : note;
        return {
            ...response,
            aiComment: withNote(response.aiComment),
            content: { ...content, contentData: kept },
            // Texte lu (F-1.8) : la suppression proposée porte sur les mails gardés.
            aiVocalText: withNote(
                buildCountMessage(
                    kept.length,
                    ACTION_PROPOSAL_VOCAL[ContentAction.DELETE],
                ),
            ),
        };
    }

    /**
     * Exécute le `toolCall` v1 d'une réponse de l'Api-Ai (contrat v1, ou fin d'un tour par étapes).
     * Contrat v1 tolérant (F-1.17) : un `toolCall` absent, un outil inconnu de cette version ou des
     * arguments illisibles donnent une réponse texte, jamais une exception.
     */
    private async executeToolCall(
        response: unknown,
        toolImplementations: ToolImplementation,
    ): Promise<ComponentData> {
        const toolCall = readToolCall(response, toolImplementations);
        if (!toolCall.ok) {
            logger.warn(
                LOGS.ConversationManager,
                `handleSingleMessage - réponse non prise en charge par cette version (${toolCall.reason}) : réponse texte`,
            );
            return buildUnsupportedResponse();
        }

        // Exécute la fonction et retourne directement son résultat
        // G4 (F-1.1) : une suppression ne vise jamais un mail déjà dans la corbeille. Appliquée ici pour
        // tous les outils v1 (recherche directe ou action sur une liste affichée), et à l'action
        // proposée en fin de tour par étapes.
        return this.applyTrashGuard(
            await toolImplementations[toolCall.name](toolCall.args),
        );
    }

    /**
     * « Arrêter » : abandonne le tour en cours (requête en vol comprise), sans toucher au reste de la
     * conversation. La réponse du tour est `stopped` ; sa question sort de l'historique. Sans tour en
     * cours : rien.
     */
    public stopTurn(): void {
        this.runningTurn?.abort();
    }

    /**
     * Pouce sous une réponse (contrat par étapes) : l'avis part avec l'identifiant du tour (`turnId`)
     * et rien d'autre, et il est gardé avec ce tour dans l'historique. Le tour y part sans attendre :
     * le pouce patiente jusqu'à ce qu'il y soit. Vrai s'il est enregistré ; faux si le tour n'est pas
     * dans l'historique ou si l'envoi échoue (journalisé).
     */
    public async sendAnswerFeedback(
        turnId: string,
        feedback: AnswerFeedback,
    ): Promise<boolean> {
        try {
            await this.pendingTurnSaves.get(turnId);
            const { enregistre } = await this.iaManager.sendAnswerFeedback(
                turnId,
                feedback === "up" ? "utile" : "pas_utile",
            );
            return enregistre === true;
        } catch (error) {
            logger.error(
                LOGS.ConversationManager,
                `sendAnswerFeedback(${turnId}) failed: ${error}`,
            );
            return false;
        }
    }

    /** Abandonne le tour en cours (requête en vol comprise) : sa réponse ne sera ni affichée ni gardée. */
    private cancelRunningTurn(): void {
        this.conversationGeneration++;
        this.runningTurn?.abort();
        this.runningTurn = undefined;
    }

    /**
     * Remet la conversation à zéro : le tour en cours est abandonné, le cache des messages, les result
     * sets (et leurs alias « Rx ») et la mémoire de la conversation sont oubliés.
     */
    private resetConversationState(): void {
        this.cancelRunningTurn();
        this.project = undefined;
        this.singleMessageCache = [];
        this.resultSetCache.clear();
        this.refCounter = 0;
        this.forgetConversationMemory();
    }

    // #region Contrat par étapes

    /**
     * Un tour du contrat par étapes (docs/contrat-etapes.md de l'Api-Ai). Tant que l'Api-Ai demande
     * des étapes, l'app les exécute et rappelle avec leurs résultats et l'état du tour, renvoyé tel
     * quel ; l'historique (`messages`) et l'identifiant du tour restent les mêmes à chaque appel.
     * Fin : la réponse de l'assistant, ses ref résolues sur ce que la conversation a lu, et la
     * proposition à confirmer ; ou un `toolCall` v1 exécuté comme en v1. Une réponse illisible donne
     * une réponse texte ; au-delà de
     * ETAPES_MAX_CALLS appels, le tour est raté (cause « service », F-1.17).
     * Raisonnement (si l'Api-Ai l'envoie) : ajouté appel après appel, émis à chaque fois
     * (`assistant-reasoning-progress`) et porté par la réponse finale (`reasoning`). Il ne part
     * jamais vers l'Api-Ai ni dans l'historique.
     * Mémoire : chaque appel porte les échanges de la conversation, tels qu'au début du tour ; la fin
     * du tour rend son échange, gardé tel quel, et la place occupée. Les refs valent pour toute la
     * conversation.
     */
    private async runEtapesTurn(
        traceId: string,
        toolImplementations: ToolImplementation,
        signal: AbortSignal,
    ): Promise<EtapesTurnResult> {
        const iaManager = this.iaManager;
        // L'historique du tour est celui de son début : une remise à zéro pendant le tour ne vide pas
        // les appels suivants (avant : `messages: []`, refusé par l'Api-Ai).
        const messages = [...this.singleMessageCache];
        const memoire = this.conversationMemory();
        // Le modèle choisi au début du tour vaut pour tous ses appels ; le projet de la conversation aussi.
        const modele = this.agentModel;
        const projet = this.project ? toEtapesProjet(this.project) : undefined;
        // La conversation du début du tour : l'Api-Backend y range ce que chaque appel consomme.
        const conversationId = this.conversationId;
        const call = (suite?: ConversationEtapesSuiteDTO) =>
            callWithOneReplay(
                () =>
                    iaManager.processConversationEtapes(
                        messages,
                        suite,
                        {
                            traceId,
                            signal,
                            modele,
                            ...(projet ? { projet } : {}),
                            conversationId,
                        },
                        memoire,
                    ),
                signal,
            );

        const reads = this.reads;
        const reasoning: ReasoningItem[] = [];
        let suite: ConversationEtapesSuiteDTO | undefined;
        // Pour l'historique : les ref déjà connues avant ce tour (il ne garde que les nouvelles).
        const knownMails = new Set(reads.mails.keys());
        const knownEvents = new Set(reads.events.keys());
        // Les noms que les contacts n'ont pas trouvés pendant ce tour : l'app les dit en tête de la réponse.
        const unknownNames: AssistantUnknownName[] = [];

        for (let callIndex = 1; ; callIndex++) {
            const response = await call(suite);
            if (signal.aborted) throw new TurnCancelledError();

            logger.info(
                LOGS.ConversationManager,
                `Réponse de l'Api-Ai (contrat par étapes, appel ${callIndex})`,
                response,
            );

            const reading = readEtapesResponse(response);
            const lignes = readRaisonnement(response);
            if (lignes.length > 0) {
                reasoning.push(...lignes);
                this.eventBus?.emit("assistant-reasoning-progress", {
                    traceId,
                    reasoning: [...reasoning],
                });
            }
            if (reading.kind !== "etapes") {
                return this.finalizeEtapesTurn(
                    reading,
                    response,
                    toolImplementations,
                    {
                        traceId,
                        signal,
                        reads,
                        reasoning,
                        knownMails,
                        knownEvents,
                        unknownNames,
                    },
                );
            }

            // Des étapes : il faut un appel de plus.
            if (callIndex >= ETAPES_MAX_CALLS) {
                throw new Error(
                    `contrat par étapes : plus de ${ETAPES_MAX_CALLS} appels pour un tour`,
                );
            }
            // Les lectures partent ensemble ; leurs éléments reçoivent leur ref ensuite, dans l'ordre
            // des étapes (des refs stables, quel que soit l'ordre d'arrivée).
            const lues = await untilCancelled(
                Promise.all(
                    reading.etapes.map((etape) => this.executeEtape(etape)),
                ),
                signal,
            );
            // Les mails reçus reçoivent leur ref avant les envoyés : un mail envoyé peut alors dire
            // auquel il répond, même quand les deux lectures partent ensemble.
            const order = lues
                .map((_, index) => index)
                .sort(
                    (a, b) =>
                        Number(isSentRead(lues[a])) -
                        Number(isSentRead(lues[b])),
                );
            const recorded = new Map<number, EtapeResultat>();
            for (const index of order) {
                recorded.set(index, this.recordEtape(lues[index], reads));
            }
            for (const lue of lues)
                addUnknownNames(unknownNames, unknownNamesOf(lue));
            const resultats: ConversationEtapesSuiteDTO["resultats"] =
                reading.etapes.map((etape, index) => ({
                    id: etape.id,
                    resultat: recorded.get(index)!,
                }));
            suite = { etat: reading.etat, resultats };
        }
    }

    /**
     * Fin d'un tour du contrat par étapes : la réponse affichée, avec son raisonnement, son modèle et
     * l'identifiant du tour. Tour réussi : son échange entre dans la mémoire, et l'historique en garde
     * l'échange et les lectures nouvelles. Tour raté (F-1.17) : rien dans la mémoire, comme dans
     * l'historique.
     */
    private async finalizeEtapesTurn(
        reading: Exclude<EtapesReading, { kind: "etapes" }>,
        response: unknown,
        toolImplementations: ToolImplementation,
        turn: {
            traceId: string;
            signal: AbortSignal;
            /** Les lectures de la conversation, telles qu'au début du tour. */
            reads: EtapesReads;
            reasoning: ReasoningItem[];
            /** Les ref déjà connues avant ce tour : l'historique ne garde que les nouvelles. */
            knownMails: Set<string>;
            knownEvents: Set<string>;
            /** Les noms introuvables dans les contacts, lus pendant ce tour. */
            unknownNames: AssistantUnknownName[];
        },
    ): Promise<EtapesTurnResult> {
        const {
            traceId,
            signal,
            reads,
            reasoning,
            knownMails,
            knownEvents,
            unknownNames,
        } = turn;
        const final = await this.finishEtapesTurn(
            reading,
            response,
            reads,
            toolImplementations,
            unknownNames,
        );
        if (signal.aborted) throw new TurnCancelledError();
        // Le tour a consommé, réussi ou non : l'Api-Backend l'a compté à chaque appel.
        this.refreshUsage();
        const failed =
            final.content.contentType === ComponentDataContentType.Error;
        let historyTurn: EtapesHistoryTurn | undefined;
        if (!failed) {
            this.rememberExchange(response);
            const { echange } = readEtapesMemoire(response);
            if (echange) {
                historyTurn = {
                    echange,
                    lectures: {
                        mails: [...reads.mails]
                            .filter(([ref]) => !knownMails.has(ref))
                            .map(([ref, read]) => ({ ref, ...read })),
                        evenements: [...reads.events]
                            .filter(([ref]) => !knownEvents.has(ref))
                            .map(([ref, item]) => ({ ref, item })),
                    },
                };
            }
        }
        const answerModel = failed ? undefined : readModeleUtilise(response);
        if (this.pendingAgenda?.reading === reading) {
            this.pendingAgenda = {
                ...this.pendingAgenda,
                turnId: traceId,
                ...(answerModel ? { answerModel } : {}),
            };
        }
        return {
            response: {
                ...final,
                ...(reasoning.length > 0 ? { reasoning } : {}),
                ...(answerModel ? { answerModel } : {}),
                ...(failed ? {} : { turnId: traceId }),
            },
            historyTurn,
        };
    }

    /** La mémoire envoyée avec un tour ou un compactage : une copie, telle qu'à cet instant. */
    private conversationMemory(): ConversationEtapesMemoireDTO {
        return { echanges: [...this.exchanges], faits: this.getFacts() };
    }

    /**
     * Fin d'un tour réussi : son échange est gardé tel quel, et la place occupée suit. Sans échange
     * (ancienne chaîne, modèle indisponible), le tour reste affiché mais n'est pas dans la mémoire.
     */
    private rememberExchange(response: unknown): void {
        const { echange, contexte, faits } = readEtapesMemoire(response);
        if (!echange) {
            logger.warn(
                LOGS.ConversationManager,
                `contrat par étapes - fin sans échange : ce tour ne sera pas dans la mémoire de la conversation`,
            );
            return;
        }
        this.exchanges.push(echange);
        if (contexte) this.setContextSize(contexte);
        // Les faits retenus après le tour, en entier ; sans ceux que l'utilisateur a fait oublier entre-temps.
        if (faits) {
            this.setFacts(
                this.forgottenDuringTurn.reduce(
                    (facts, key) => withoutFact(facts, key),
                    faits,
                ),
            );
        }
    }

    private setContextSize(size: ConversationContextSize | undefined): void {
        this.contextSize = size;
        this.eventBus?.emit("assistant-context-size", size ?? null);
    }

    private setFacts(facts: ConversationFact[]): void {
        if (sameFacts(facts, this.facts)) return;
        this.facts = facts;
        this.eventBus?.emit("assistant-facts", this.getFacts());
    }

    /**
     * Envoie les faits à l'historique s'ils ont changé depuis le dernier envoi, sans attendre. Une
     * conversation que l'historique ne connaît pas encore attend son premier tour gardé. Un seul envoi
     * à la fois : un changement pendant l'envoi repart à sa fin, avec la liste du moment. Un échec n'est
     * que journalisé : les faits repartiront au prochain changement.
     */
    private persistFacts(): void {
        const iaManager = this.iaManager;
        if (
            this.conversationContract !== "etapes" ||
            !this.conversationSaved ||
            sameFacts(this.facts, this.savedFacts)
        ) {
            return;
        }
        if (this.sendingFacts) {
            this.factsToSend = true;
            return;
        }
        this.sendingFacts = true;
        const conversationId = this.conversationId;
        const facts = this.getFacts();
        void iaManager
            .setConversationMemory(conversationId, facts)
            .then(() => {
                if (conversationId === this.conversationId) {
                    this.savedFacts = facts;
                }
            })
            .catch((error: unknown) =>
                logger.warn(
                    LOGS.ConversationManager,
                    `historique : faits retenus non gardés (${error})`,
                ),
            )
            .finally(() => {
                this.sendingFacts = false;
                if (this.factsToSend) {
                    this.factsToSend = false;
                    this.persistFacts();
                }
            });
    }

    private setUsage(usage: AssistantUsage | undefined): void {
        this.usage = usage;
        this.eventBus?.emit("assistant-usage", usage ?? null);
    }

    /**
     * Relit à l'Api-Backend ce que l'assistant a consommé (la conversation en cours et le mois), sans
     * attendre : un échec, une réponse arrivée après un changement de conversation, ou après une
     * relecture plus récente, garde ce qui était affiché. Jamais une erreur vers l'appelant : le
     * compteur n'arrête pas un tour.
     */
    private refreshUsage(): void {
        const iaManager = this.iaManager;
        if (!this.hasConversationMemory()) return;
        const conversationId = this.conversationId;
        const read = ++this.usageReads;
        void iaManager
            .getAssistantUsage(conversationId)
            .then((response) => {
                const usage = readAssistantUsage(response);
                if (
                    usage &&
                    conversationId === this.conversationId &&
                    read === this.usageReads
                ) {
                    this.setUsage(usage);
                }
            })
            .catch((error: unknown) => {
                logger.warn(
                    LOGS.ConversationManager,
                    `refreshUsage() - compteur de consommation non lu : ${error}`,
                );
            });
    }

    /** « Nouvelle conversation » : échanges, place et lectures oubliés ; une autre conversation commence. */
    private forgetConversationMemory(): void {
        this.exchanges = [];
        this.pendingContactQuestion = undefined;
        this.pendingAgenda = undefined;
        this.contactResolutions.clear();
        this.unavailableContacts.clear();
        this.savedFacts = [];
        this.conversationSaved = false;
        this.forgottenDuringTurn = [];
        this.setFacts([]);
        this.reads = emptyReads();
        this.setContextSize(undefined);
        this.setUsage(undefined);
        this.conversationId = generateUUID();
    }

    // #region Historique des conversations (contrat par étapes)

    /** La conversation en cours, telle que l'historique la connaît. */
    public getConversationId(): string {
        return this.conversationId;
    }

    /**
     * Un tour réussi du contrat par étapes part à l'historique, avec son affichage (sans le corps des
     * mails), ses lectures et son échange. Sans attendre : un échec n'est que journalisé, la
     * conversation continue.
     */
    private saveTurnToHistory(
        question: string,
        traceId: string,
        response: ComponentData,
        turn: EtapesHistoryTurn | undefined,
    ): void {
        if (!turn) return;
        const saved = this.persistHistory(`tour ${traceId}`, (contexte) => {
            const payload: HistoryTurnPayload = toHistoryValue({
                affichage: response,
                lectures: turn.lectures,
                ...contexte,
            });
            return {
                kind: "tour",
                turnId: traceId,
                question: questionForHistory(question),
                payload,
                echange: turn.echange,
            };
        });
        this.pendingTurnSaves.set(traceId, saved);
        void saved.finally(() => this.pendingTurnSaves.delete(traceId));
    }

    /** Un compactage part à l'historique : la suite de la conversation repart de son résumé. */
    private saveCompactionToHistory(echange: string, resume: string): void {
        void this.persistHistory("compactage", (contexte) => {
            const payload: HistoryCompactionPayload = { resume, ...contexte };
            return { kind: "compactage", payload, echange };
        });
    }

    /**
     * Envoie à l'historique un tour ou un compactage de la conversation en cours (contrat par étapes
     * seulement), avec la place occupée s'il y en a une. Sans attendre : un échec n'est que journalisé
     * (`historique : <what> non gardé`). La promesse rendue se termine avec l'envoi, sans jamais échouer.
     */
    private persistHistory(
        what: string,
        build: (contexte: {
            contexte?: ConversationContextSize;
        }) => Omit<ConversationTurnSaveDTO, "conversationId">,
    ): Promise<void> {
        if (this.conversationContract !== "etapes") {
            return Promise.resolve();
        }
        const entry = build(
            this.contextSize ? { contexte: this.contextSize } : {},
        );
        const conversationId = this.conversationId;
        // Le projet de la conversation : l'Api-Backend ne le lit qu'à sa création (son premier tour).
        const projectId = this.project?.id;
        return this.iaManager
            .saveConversationTurn({
                conversationId,
                ...(projectId ? { projectId } : {}),
                ...entry,
            })
            .then(() => {
                this.eventBus?.emit("assistant-conversation-saved", {
                    conversationId,
                });
                // La conversation est dans l'historique : ses faits peuvent y être gardés.
                if (conversationId === this.conversationId) {
                    this.conversationSaved = true;
                    this.persistFacts();
                }
            })
            .catch((error: unknown) =>
                logger.error(
                    LOGS.ConversationManager,
                    `historique : ${what} non gardé (${error})`,
                ),
            );
    }

    /**
     * Les conversations de l'historique, la plus récente d'abord ; avec `query` (deux caractères au
     * moins), celles où ce mot clé apparaît, chacune avec un extrait.
     */
    public async listConversations(
        query?: string,
    ): Promise<ConversationSummary[]> {
        const motCle = query?.trim();
        return this.iaManager.listConversations(
            motCle && motCle.length >= 2 ? motCle : undefined,
        );
    }

    /**
     * Rouvre une conversation de l'historique : le tour en cours est abandonné, puis la mémoire
     * (échanges depuis le dernier compactage) et les lectures (par ref) reprennent là où elle s'était
     * arrêtée ; la question suivante continue cette conversation. Rend ce qu'il faut réafficher,
     * pouces déjà donnés compris.
     * Introuvable ou illisible : `null`, rien ne change.
     */
    public async openConversation(
        id: string,
    ): Promise<RestoredConversation | null> {
        let detail: ConversationDetailDTO;
        try {
            detail = await this.iaManager.getConversation(id);
        } catch (error) {
            logger.error(
                LOGS.ConversationManager,
                `openConversation(${id}) - illisible : ${error}`,
            );
            return null;
        }
        // Conversation rangée dans un projet : sans lui, ses lectures ne seraient plus limitées à son
        // périmètre. Projet illisible : la conversation ne se rouvre pas.
        let project: AssistantProject | undefined;
        if (detail.projectId) {
            try {
                project = (await this.iaManager.listProjects()).find(
                    (candidate) => candidate.id === detail.projectId,
                );
            } catch (error) {
                logger.error(
                    LOGS.ConversationManager,
                    `openConversation(${id}) - projet illisible : ${error}`,
                );
                return null;
            }
        }
        this.resetConversationState();
        this.conversationId = detail.id;
        this.project = project;

        const items: RestoredConversationItem[] = [];
        const feedback: Record<string, AnswerFeedback> = {};
        let memoire: string[] = [];
        // La jauge : la dernière place gardée (d'un tour ou d'un compactage).
        let contexte: ConversationContextSize | undefined;
        for (const turn of detail.turns ?? []) {
            contexte = readHistoryContext(turn.payload) ?? contexte;
            if (turn.kind === "compactage") {
                // Seul le dernier compactage porte son échange : les précédents sont remplacés par lui.
                memoire = turn.echange === null ? [] : [turn.echange];
                items.push({
                    kind: "compaction",
                    summary: readHistoryCompaction(turn.payload),
                    at: turn.createdAt,
                });
                continue;
            }
            const lu = readHistoryTurn(turn.payload);
            if (!lu || !turn.question) continue;
            if (turn.echange !== null) memoire.push(turn.echange);
            for (const { ref, mail, filter, criteria } of lu.lectures.mails) {
                this.reads.mails.set(ref, { mail, filter, criteria });
                this.reads.refs.set(
                    `mail:${mail.uniqueMailvistaId ?? uniqueMailvistaId(mail)}`,
                    ref,
                );
            }
            for (const { ref, item } of lu.lectures.evenements) {
                this.reads.events.set(ref, item);
                this.reads.refs.set(`evenement:${item.id}`, ref);
            }
            this.singleMessageCache.push(
                { role: "user", content: turn.question },
                { role: "assistant", content: this.historyText(lu.affichage) },
            );
            items.push({
                kind: "turn",
                question: turn.question,
                response: lu.affichage,
            });
            const thumb = FEEDBACK_BY_AVIS[turn.avis ?? ""];
            if (turn.turnId && thumb) feedback[turn.turnId] = thumb;
        }
        this.exchanges = memoire;
        this.savedFacts = readFacts(detail.faits) ?? [];
        this.conversationSaved = true;
        this.setFacts(this.savedFacts);
        this.setContextSize(contexte);
        this.refreshUsage();
        logger.info(
            LOGS.ConversationManager,
            `openConversation(${id}) - ${items.length} éléments, ${memoire.length} échanges en mémoire`,
        );
        return {
            id: detail.id,
            title: detail.title,
            items,
            feedback,
            ...(project ? { project } : {}),
        };
    }

    public async renameConversation(id: string, title: string): Promise<void> {
        await this.iaManager.renameConversation(id, title);
    }

    /** Supprime une conversation de l'historique ; la conversation en cours : une autre commence. */
    public async deleteConversation(id: string): Promise<void> {
        await this.iaManager.deleteConversation(id);
        if (id === this.conversationId) this.resetSingleMessageCache();
    }

    /** Supprime tout l'historique ; la conversation en cours recommence aussi. */
    public async deleteAllConversations(): Promise<void> {
        await this.iaManager.deleteAllConversations();
        this.resetSingleMessageCache();
    }

    // #endregion

    // #region Projets de l'assistant (contrat par étapes)

    /** Le projet où la conversation en cours est rangée, s'il y en a un. */
    public getCurrentProject(): AssistantProject | undefined {
        return this.project;
    }

    /** Les projets de l'utilisateur, le plus récemment modifié d'abord, avec leur nombre de conversations. */
    public async listProjects(): Promise<AssistantProject[]> {
        return this.iaManager.listProjects();
    }

    public async createProject(
        project: AssistantProjectInput,
    ): Promise<AssistantProject> {
        return this.iaManager.createProject(project);
    }

    /**
     * Modifie les seuls champs donnés. Le projet de la conversation en cours : ses nouvelles consignes
     * et son nouveau périmètre valent dès le tour suivant.
     */
    public async updateProject(
        id: string,
        changes: Partial<AssistantProjectInput>,
    ): Promise<AssistantProject> {
        const updated = await this.iaManager.updateProject(id, changes);
        if (this.project?.id === id) this.project = updated;
        return updated;
    }

    /**
     * Supprime un projet : ses conversations reviennent à la liste générale ; la conversation en cours,
     * si elle y était rangée, continue hors projet.
     */
    public async deleteProject(id: string): Promise<void> {
        await this.iaManager.deleteProject(id);
        if (this.project?.id === id) this.project = undefined;
    }

    /**
     * Une nouvelle conversation dans un projet : la conversation en cours est quittée, la suivante suit
     * les consignes et le périmètre du projet, et y sera rangée à son premier tour.
     */
    public startProjectConversation(project: AssistantProject): void {
        this.resetSingleMessageCache();
        this.project = project;
    }

    /**
     * Range une conversation de l'historique dans un projet, ou l'en sort (`null`). La conversation en
     * cours : elle suit le projet (ou cesse de le suivre) dès le tour suivant.
     */
    public async moveConversation(
        conversationId: string,
        project: AssistantProject | null,
    ): Promise<void> {
        await this.iaManager.setConversationProject(
            conversationId,
            project?.id ?? null,
        );
        if (conversationId === this.conversationId) {
            this.project = project ?? undefined;
        }
    }

    // #endregion

    /**
     * Fin d'un tour du contrat par étapes : ce que l'app affiche, selon la réponse de l'Api-Ai — la
     * réponse de l'assistant (et sa proposition), ou un `toolCall` v1.
     */
    private async finishEtapesTurn(
        reading: Exclude<EtapesReading, { kind: "etapes" }>,
        response: unknown,
        reads: EtapesReads,
        toolImplementations: ToolImplementation,
        unknownNames: AssistantUnknownName[],
    ): Promise<ComponentData> {
        switch (reading.kind) {
            case "toolCall":
                return this.executeToolCall(response, toolImplementations);
            case "reponse":
                return this.buildEtapesAnswerResponse(
                    reading,
                    reads,
                    toolImplementations,
                    unknownNames,
                );
            case "illisible":
                logger.warn(
                    LOGS.ConversationManager,
                    `contrat par étapes - réponse non prise en charge par cette version (${reading.reason}) : réponse texte`,
                );
                return buildUnsupportedResponse();
        }
    }

    /**
     * La réponse de l'assistant (docs/refonte-assistant/CONCEPTION-reponse-unique.md de l'Api-Ai) :
     * son texte, ses ref résolues sur ce que la conversation a lu (mails, événements) et sur les
     * créneaux cités ; puis la proposition à confirmer, construite comme en v1 (mails d'une action,
     * avec les gardes de masse et de la corbeille ; rendez-vous). Ce que le code a à dire de la
     * proposition (mails déjà dans la corbeille, participants introuvables) s'ajoute à la réponse.
     */
    private async buildEtapesAnswerResponse(
        reading: Extract<EtapesReading, { kind: "reponse" }>,
        reads: EtapesReads,
        toolImplementations: ToolImplementation,
        unknownNames: AssistantUnknownName[] = [],
    ): Promise<ComponentData> {
        const proposal = await this.buildEtapesProposal(
            reading,
            reads,
            toolImplementations,
        );
        // La question du tour : celle que l'app pose avant de préparer la proposition (un participant
        // à préciser), sinon celle de la fin du tour. Posée sur un contact, sa réponse le fixera.
        const question = proposal?.question?.question ?? reading.question;
        // Un participant à préciser : le rendez-vous attend le choix de l'utilisateur (R3).
        this.pendingAgenda = proposal?.question
            ? { reading, reads, question: proposal.question }
            : undefined;
        this.pendingContactQuestion =
            proposal?.question ??
            this.askedContactQuestions.find(
                (asked) => asked.question.question === question?.question,
            );
        const text = [reading.message.trim(), ...(proposal?.notes ?? [])]
            .filter(Boolean)
            .join("\n\n");
        const base: ComponentData = proposal?.response ?? {
            aiComment: "",
            userRequestId: generateUUID(),
            content: {
                contentData: "",
                contentType: ComponentDataContentType.BasicAiChat,
                contentAction: ContentAction.VIEW,
                upcomingAnalysis: false,
            },
            aiVocalText: "",
        };
        return {
            ...base,
            aiComment: text,
            // Lu à voix haute : la réponse ; avec une proposition, le texte du code (F-1.8 : une
            // proposition à confirmer, jamais « je supprime »).
            aiVocalText: proposal?.response
                ? base.aiVocalText
                : spokenAnswer(text),
            answer: {
                ...this.resolveAnswer(text, reading, reads),
                // Les noms introuvables, dits par l'app en tête de la réponse (pas lus à voix haute).
                ...(unknownNames.length > 0 ? { unknownNames } : {}),
                ...(question
                    ? {
                          question: {
                              question: question.question,
                              choices: question.choix.map((choix) => ({
                                  label: choix.libelle,
                                  ...(choix.detail
                                      ? { detail: choix.detail }
                                      : {}),
                                  reply: choix.reponse ?? choix.libelle,
                              })),
                          },
                      }
                    : {}),
            },
        };
    }

    /**
     * Les éléments que la réponse cite, par ref : une ref inconnue n'y est pas, elle reste du texte. Le
     * brouillon de mail, s'il y en a un, avec le mail auquel il répond.
     */
    private resolveAnswer(
        text: string,
        reading: Extract<EtapesReading, { kind: "reponse" }>,
        reads: EtapesReads,
    ): AssistantAnswer {
        const slots = new Map(
            reading.creneaux.map((creneau) => [creneau.ref, creneau.slot]),
        );
        const replies = new Map(
            reading.creneaux.flatMap((creneau): [string, string][] =>
                creneau.reply ? [[creneau.ref, creneau.reply]] : [],
            ),
        );
        const slotReplies: Record<string, string> = {};
        const answer: AssistantAnswer = {
            text,
            mails: {},
            events: {},
            slots: {},
        };
        const sentMails = new Set<string>();
        for (const [, ref] of text.matchAll(ANSWER_REF)) {
            const read = reads.mails.get(ref);
            const event = reads.events.get(ref);
            const slot = slots.get(ref);
            if (read) {
                answer.mails[ref] = read.mail;
                if (read.criteria.boite === "envoyes") sentMails.add(ref);
            } else if (event) answer.events[ref] = event;
            else if (slot) {
                answer.slots[ref] = slot;
                const reply = replies.get(ref);
                if (reply) slotReplies[ref] = reply;
            }
        }
        if (sentMails.size > 0) answer.sentMails = [...sentMails];
        if (Object.keys(slotReplies).length > 0) {
            answer.slotReplies = slotReplies;
        }
        const draft = this.resolveDraft(reading.brouillon, reads);
        if (draft) answer.draft = draft;
        return answer;
    }

    /**
     * Le brouillon de mail : une réponse, avec le mail lu auquel elle répond, ou un nouveau mail. Une
     * réponse à un mail que la conversation n'a pas lu n'a rien à quoi répondre : laissée de côté, la
     * réponse reste.
     */
    private resolveDraft(
        brouillon: EtapesBrouillon | undefined,
        reads: EtapesReads,
    ): AssistantDraft | undefined {
        if (!brouillon) return undefined;
        if (brouillon.reponseA) {
            const replyTo = reads.mails.get(brouillon.reponseA)?.mail;
            return replyTo ? { body: brouillon.texte, replyTo } : undefined;
        }
        return {
            body: brouillon.texte,
            ...(brouillon.objet ? { subject: brouillon.objet } : {}),
        };
    }

    /**
     * La proposition de la fin du tour, à confirmer : l'action sur les mails visés, ou le rendez-vous,
     * et ce que le code en dit (`notes`). Une proposition que cette version ne sait pas lire est laissée
     * de côté : la réponse reste.
     */
    private async buildEtapesProposal(
        reading: Extract<EtapesReading, { kind: "reponse" }>,
        reads: EtapesReads,
        toolImplementations: ToolImplementation,
    ): Promise<
        | {
              response?: ComponentData;
              notes: string[];
              /** La question posée à la place de la proposition, tant qu'un contact reste à préciser. */
              question?: ContactQuestion;
          }
        | undefined
    > {
        if (reading.action) {
            // Action inconnue de cette version : pas de bouton (F-1.6).
            const userAction = this.stringToContentActionFromKey(
                reading.action.action,
            );
            if (userAction === undefined) {
                logger.warn(
                    LOGS.ConversationManager,
                    `contrat par étapes - action inconnue de cette version : laissée de côté`,
                );
                return undefined;
            }
            const response = this.buildEtapesMailsResponse(
                reading.action.refs,
                reads.mails,
                userAction,
            );
            // G2 (masse) : la liste reste en consultation, et le code le dit.
            const massNote =
                response.content.contentAction !== userAction
                    ? response.aiComment
                    : "";
            // G4 (F-1.1) : une suppression ne vise jamais un mail déjà dans la corbeille ; sans
            // commentaire, ce que la garde ajoute est sa note.
            const guarded = this.applyTrashGuard({
                ...response,
                aiComment: "",
            });
            return {
                response: guarded,
                notes: [massNote, guarded.aiComment].filter(Boolean),
            };
        }
        if (reading.rendezVous) {
            const toolCall = readToolCall(
                { toolCall: reading.rendezVous },
                toolImplementations,
            );
            if (!toolCall.ok || toolCall.name !== "createAgendaItem") {
                logger.warn(
                    LOGS.ConversationManager,
                    `contrat par étapes - rendez-vous non pris en charge par cette version : laissé de côté`,
                );
                return undefined;
            }
            // Un participant à préciser (deux contacts pour un nom, un nom qui ressemble à un contact) :
            // l'app pose la question au lieu d'inviter le premier venu, et le rendez-vous attend. Au
            // clic sur un choix, c'est l'app qui le termine (`finishPendingAgenda`, R3).
            const question = await this.attendeesQuestion(
                (toolCall.args as unknown as CreateAgendaItemParams)
                    .attendees ?? [],
            );
            if (question) {
                return { notes: [ATTENDEE_TO_CONFIRM_NOTE], question };
            }
            // Sans commentaire, ce que l'app ajoute est sa note (participants introuvables, F-1.13).
            const response = await toolImplementations.createAgendaItem({
                ...(toolCall.args as unknown as CreateAgendaItemParams),
                aiComment: "",
            });
            if (
                response.content.contentType === ComponentDataContentType.Error
            ) {
                return undefined;
            }
            return { response, notes: [response.aiComment].filter(Boolean) };
        }
        return undefined;
    }

    /**
     * Exécute une étape demandée par l'Api-Ai, avec le code actuel de l'app. Étape inconnue de cette
     * version : `{ erreur: "etape_inconnue" }`.
     */
    private async executeEtape(etape: Etape): Promise<EtapeLue> {
        switch (etape.outil) {
            case "lireAgenda":
                return this.executeLireAgenda(etape.arguments);
            case "lireMails":
                return this.executeLireMails(etape.arguments);
            default:
                logger.warn(
                    LOGS.ConversationManager,
                    `contrat par étapes - étape inconnue de cette version (${etape.outil})`,
                );
                return { erreur: "etape_inconnue" };
        }
    }

    /**
     * Ce que l'Api-Ai reçoit d'une étape lue : chaque élément rendu reçoit sa ref (« e1 », « m1 »…), la
     * même s'il a déjà été lu pendant la conversation, et il est gardé par ref pour l'affichage final
     * (de ce tour ou d'un tour suivant).
     */
    private recordEtape(lue: EtapeLue, reads: EtapesReads): EtapeResultat {
        if ("erreur" in lue) {
            return lue.erreur === "contacts_indisponibles"
                ? { erreur: lue.erreur, personnes: lue.personnes }
                : { erreur: lue.erreur };
        }
        if (lue.outil === "lireAgenda") {
            return buildLireAgendaResultat(lue.items, (item) => {
                const ref = this.refOf(reads, `evenement:${item.id}`, "e");
                reads.events.set(ref, item);
                return ref;
            });
        }
        if ("question" in lue) return { question: lue.question };
        const { found, limit, filter, criteria, people, scope } = lue;
        const sent = isSentRead(lue);
        // Les mails reçus que la conversation a déjà lus : un mail envoyé dit auquel il répond.
        const received = [...reads.mails].flatMap(
            ([ref, read]): [string, Mail][] =>
                read.criteria.boite === "envoyes" ? [] : [[ref, read.mail]],
        );
        // Un mail de plus que la limite : il dit si la lecture est complète.
        const tronque = found.length > limit;
        const rendus = found.slice(0, limit).map((mail) => {
            const ref = this.refOf(
                reads,
                `mail:${mail.uniqueMailvistaId ?? uniqueMailvistaId(mail)}`,
                "m",
            );
            reads.mails.set(ref, { mail, filter, criteria });
            return sent
                ? toEtapeMailEnvoye(mail, ref, repliedRef(mail, received))
                : toEtapeMail(mail, ref);
        });
        const personnes = people.length > 0 ? { personnes: people } : {};
        // L'app dit la boîte qu'elle a lue : « aucun mail envoyé » ne se lit pas « aucun mail ».
        const boite = sent ? ({ boite: "envoyes" } as const) : {};
        // Et, dans un projet, ce que son périmètre a fait de la lecture.
        const perimetre = scope ? { perimetre: scope } : {};
        return tronque
            ? { mails: rendus, tronque, ...personnes, ...boite, ...perimetre }
            : {
                  mails: rendus,
                  total: rendus.length,
                  tronque,
                  ...personnes,
                  ...boite,
                  ...perimetre,
              };
    }

    /** Ref d'un élément pour la conversation : celle déjà donnée, sinon la suivante de son type. */
    private refOf(
        reads: EtapesReads,
        identity: string,
        prefix: "e" | "m",
    ): string {
        const known = reads.refs.get(identity);
        if (known) return known;
        const count = prefix === "e" ? reads.events.size : reads.mails.size;
        const ref = `${prefix}${count + 1}`;
        reads.refs.set(identity, ref);
        return ref;
    }

    /**
     * Étape `lireAgenda` : les événements de la période, lus par l'AgendaManager (Graph, Google).
     * Lecture impossible : `{ erreur: "agenda_illisible" }`, jamais une liste vide — une panne ne vaut
     * jamais « tout est libre » (F-1.11).
     */
    private async executeLireAgenda(args: unknown): Promise<EtapeLue> {
        try {
            const { debut, fin } = (args ?? {}) as {
                debut?: unknown;
                fin?: unknown;
            };
            if (!isDate(debut) || !isDate(fin)) {
                throw new Error("début ou fin illisible");
            }
            const items = await this.agendaManager.getUserAgenda(debut, fin);
            return { outil: "lireAgenda", items };
        } catch (error) {
            logger.error(
                LOGS.ConversationManager,
                `contrat par étapes - lecture de l'agenda impossible : ${error}`,
            );
            return { erreur: "agenda_illisible" };
        }
    }

    /**
     * Étape `lireMails` : les mails qui répondent aux critères de `searchMails` (dates déjà en UTC),
     * cherchés comme pour `searchMails`, `LIRE_MAILS_MAX` au plus (`tronque` au-delà), jamais leur
     * contenu, et comment chaque nom demandé a été compris. Lecture impossible :
     * `{ erreur: "boite_illisible" }`, jamais une liste vide.
     */
    private async executeLireMails(args: unknown): Promise<EtapeLue> {
        try {
            if (
                typeof args !== "object" ||
                args === null ||
                Array.isArray(args)
            ) {
                throw new Error("critères illisibles");
            }
            const criteria = args as SearchMailsParams;
            // Période illisible ou inversée : pas de lecture, l'ignorer l'élargirait sans le dire.
            const period = readSearchPeriod(
                criteria.startDate,
                criteria.endDate,
            );
            if (period.status !== "ok") {
                throw new Error("période illisible");
            }
            // Une lecture porte sur une seule boîte. Les mails envoyés n'ont ni expéditeur à filtrer,
            // ni vue, ni catégorie : un tel critère, ignoré, élargirait la lecture sans le dire.
            const sent = criteria.boite === "envoyes";
            if (
                (criteria.boite !== undefined &&
                    criteria.boite !== "recus" &&
                    !sent) ||
                (sent && hasReceivedOnlyCriteria(criteria))
            ) {
                throw new Error("boîte ou critères illisibles");
            }
            const {
                filter: requestedFilter,
                people,
                question,
            } = await this.buildSearchFilter(criteria, period);
            // Un nom qui n'a pas pu être cherché ne part pas en lecture sous sa forme brute : rien
            // n'est lu, et l'assistant dit que la recherche dans les contacts n'a pas pu se faire.
            if (people.some((person) => person.resultat === "indisponible")) {
                return { erreur: "contacts_indisponibles", personnes: people };
            }
            if (question) {
                // Deux contacts pour un nom, ou un nom introuvable qui ressemble à un contact : l'app
                // ne choisit pas à la place de l'utilisateur, elle rend la question au lieu de lire.
                this.askedContactQuestions.push(question);
                return { outil: "lireMails", question: question.question };
            }
            const limit = Math.min(
                criteria.limit && criteria.limit > 0
                    ? criteria.limit
                    : LIRE_MAILS_MAX,
                LIRE_MAILS_MAX,
            );
            // Conversation d'un projet : la lecture ne porte que sur les mails échangés avec les
            // adresses de son périmètre. Les adresses demandées en sortent toutes : rien n'est lu.
            const { filter, outcome: scope } = applyMailScope(
                requestedFilter,
                this.project?.mailScope ?? [],
                sent,
            );
            if (scope === "hors") {
                return {
                    outil: "lireMails",
                    found: [],
                    limit,
                    filter,
                    criteria,
                    people,
                    scope,
                };
            }
            const found = sent
                ? await this.mailManager.getSentMails(
                      {
                          recipients: filter.identifiedRecipients ?? [],
                          keywords: filter.keywords,
                          startDate: filter.startDate,
                          endDate: filter.endDate,
                          hasAttachment: filter.hasAttachment,
                          sortOrder:
                              filter.sortOrder === CustomViewSortOrder.ASC
                                  ? "ASC"
                                  : "DESC",
                      },
                      limit + 1,
                  )
                : await this.mailManager.getViewFilterMails(filter, limit + 1);
            return {
                outil: "lireMails",
                found,
                limit,
                filter,
                criteria,
                people,
                ...(scope ? { scope } : {}),
            };
        } catch (error) {
            logger.error(
                LOGS.ConversationManager,
                `contrat par étapes - lecture des mails impossible : ${error}`,
            );
            return { erreur: "boite_illisible" };
        }
    }

    /**
     * Les mails d'une action proposée : ceux lus pendant la conversation, dans l'ordre des `refs` (une
     * ref inconnue est ignorée), dans la même réponse qu'une recherche v1 : liste mémorisée pour le
     * bloc [ref:Rx …] de l'historique, action à confirmer à l'écran sauf au-delà du seuil de masse (G2).
     */
    private buildEtapesMailsResponse(
        refs: string[],
        mailsRead: EtapesReads["mails"],
        userAction: ContentAction,
    ): ComponentData {
        const shown = [...new Set(refs)].filter((ref) => mailsRead.has(ref));
        const reads = shown
            .map((ref) => mailsRead.get(ref))
            .filter((read) => read !== undefined);
        const mails = reads.map((read) => read.mail);
        const userRequestId = generateUUID();

        // Liste mémorisée comme pour une recherche v1, avec les critères de sa lecture.
        if (reads.length > 0) {
            this.rememberResultSet(
                userRequestId,
                mails,
                reads[0].filter,
                reads[0].criteria,
            );
        }

        // G2 (F-1.3) : au-delà du seuil de masse, pas d'action groupée — la liste reste en consultation.
        const isGroupAction = userAction !== ContentAction.VIEW;
        const { isMassAction, contentAction, massComment } = groupActionGuard(
            mails.length,
            userAction,
        );
        const aiComment =
            massComment ?? buildCountMessage(mails.length, MAIL_SEARCH_COMMENT);

        return {
            aiComment,
            userRequestId,
            content: {
                contentData: mails,
                contentType: ComponentDataContentType.Mails,
                contentAction,
                upcomingAnalysis: false,
            },
            // Texte lu (F-1.8) : une action groupée est proposée, à confirmer à l'écran — jamais
            // « je supprime ».
            aiVocalText: isMassAction
                ? aiComment
                : buildCountMessage(
                      mails.length,
                      isGroupAction
                          ? ACTION_PROPOSAL_VOCAL[userAction]
                          : MAIL_SEARCH_VOCAL,
                  ),
        };
    }

    // #endregion Contrat par étapes

    /**
     * Texte d'une réponse pour l'historique : le commentaire, et selon l'affichage le nombre de
     * résultats, le bloc [ref:Rx …] d'une liste de mails mémorisée, ou les créneaux.
     */
    private historyText(response: ComponentData): string {
        // Contrat par étapes : la réponse de l'assistant, telle qu'affichée (ses ref comprises).
        if (response.answer) return response.answer.text;
        switch (response.content.contentType) {
            case ComponentDataContentType.AgendaItems:
                return Array.isArray(response.content.contentData)
                    ? response.aiComment +
                          " ( " +
                          response.content.contentData.length +
                          " résultats trouvés ) "
                    : response.aiComment;

            case ComponentDataContentType.Mails: {
                const aiMessage = Array.isArray(response.content.contentData)
                    ? response.aiComment +
                      " ( " +
                      response.content.contentData.length +
                      " résultats trouvés ) "
                    : response.aiComment;

                // Concaténation du bloc de référence [ref:Rx | type:mails | ...] si cette
                // recherche a alimenté le resultSetCache (searchMails réussie et non vide).
                // Ce bloc, transmis dans l'historique, permet à l'IA de référencer ce result
                // set via applyActionToPreviousResults au tour suivant.
                const matchingRefEntry = [...this.resultSetCache.values()].find(
                    (entry) => entry.userRequestId === response.userRequestId,
                );
                return matchingRefEntry
                    ? aiMessage + matchingRefEntry.refBlock
                    : aiMessage;
            }

            case ComponentDataContentType.FreeTimeSlots:
                if (Array.isArray(response.content.contentData)) {
                    let aiTimeSlotMessage = "";
                    (response.content.contentData as AgendaTimeSlot[]).forEach(
                        (timeSlot) => {
                            aiTimeSlotMessage +=
                                this.formatFreeTimeSlotDateRange(
                                    new Date(timeSlot.startDate),
                                    new Date(timeSlot.endDate),
                                ) + "   \n";
                        },
                    );
                    return response.aiComment + " " + aiTimeSlotMessage;
                }
                return response.aiComment;

            case ComponentDataContentType.FallBackResponse:
                return response.aiComment + " " + response.content.contentData;

            default:
                return response.aiComment;
        }
    }

    /**
     * Tour raté (F-1.17) : retire du cache la question de ce tour, sans rien écrire côté assistant.
     * L'entrée est repérée par identité : si le cache a été remis à zéro pendant l'appel, rien
     * d'autre n'est touché.
     */
    private removeFailedTurn(userEntry: {
        role: "user";
        content: string;
    }): void {
        const index = this.singleMessageCache.lastIndexOf(userEntry);
        if (index !== -1) {
            this.singleMessageCache.splice(index, 1);
        }
    }

    /**
     * Arguments qu'un outil texte ne sait pas lire (contrat v1 tolérant, F-1.17) : réponse texte
     * plutôt qu'une exception, et un log (nom de l'outil seulement).
     */
    private unsupportedTextToolResponse(
        toolName: string,
        error: unknown,
    ): ComponentData {
        logger.warn(
            LOGS.ConversationManager,
            `${toolName} - arguments non pris en charge par cette version (${error}) : réponse texte`,
        );
        return buildUnsupportedResponse();
    }

    /**
     * Construit le bloc de référence "[ref:Rx | type:mails | n:N | filtres: ...]" concaténé
     * au message assistant de l'historique (contrat parsé par les prompts de l'API AI).
     * Le résumé des filtres reprend les critères de la recherche (keywords déjà enrichis par l'API).
     */
    private buildResultSetRefBlock(
        refAlias: string,
        resultCount: number,
        args: SearchMailsParams,
    ): string {
        const filterParts: string[] = [];

        if (args.from && args.from.length > 0) {
            filterParts.push(`from=${args.from.join(",")}`);
        }
        if (args.to && args.to.length > 0) {
            filterParts.push(`to=${args.to.join(",")}`);
        }
        if (args.keywords && args.keywords.length > 0) {
            filterParts.push(`keywords=${args.keywords.join(",")}`);
        }
        if (args.startDate) {
            filterParts.push(`startDate=${args.startDate}`);
        }
        if (args.endDate) {
            filterParts.push(`endDate=${args.endDate}`);
        }
        if (args.categories && args.categories.length > 0) {
            filterParts.push(`categories=${args.categories.join(",")}`);
        }
        if (args.mailStatus && args.mailStatus.length > 0) {
            filterParts.push(`mailStatus=${args.mailStatus.join(",")}`);
        }
        if (args.readStatus) {
            filterParts.push(`readStatus=${args.readStatus}`);
        }
        if (args.hasAttachment !== undefined) {
            filterParts.push(`hasAttachment=${args.hasAttachment}`);
        }

        const filterSummary =
            filterParts.length > 0 ? filterParts.join("; ") : "aucun";

        return `[ref:${refAlias} | type:mails | n:${resultCount} | filtres: ${filterSummary}]`;
    }

    private stringToContentActionFromKey(
        actionKey: string,
    ): ContentAction | undefined {
        // Vérifier si la clé existe dans l'enum : clé PROPRE seulement — « toString » ou
        // « constructor », hérités, ne sont pas des actions (F-1.6)
        if (Object.prototype.hasOwnProperty.call(ContentAction, actionKey)) {
            // Utiliser une assertion de type pour éviter l'erreur
            const enumKey = actionKey as keyof typeof ContentAction;
            return ContentAction[enumKey];
        }

        return undefined;
    }

    /**
     * @method formatDuration
     * Formate une durée en minutes en format lisible (heures et minutes)
     * ═══════════════════════════════════════════════════════════════════════════
     * @input {number} minutes - Durée en minutes
     * @output {string} Durée formatée (ex: "2h 30min", "45min")
     * @private
     */
    private formatDuration(minutes: number): string {
        if (minutes < 60) {
            return `${minutes} minute${minutes > 1 ? "s" : ""}`;
        }

        const hours = Math.floor(minutes / 60);
        const remainingMinutes = minutes % 60;

        if (remainingMinutes === 0) {
            return `${hours} heure${hours > 1 ? "s" : ""}`;
        }

        return `${hours} heure${hours > 1 ? "s" : ""} ${remainingMinutes} minute${remainingMinutes > 1 ? "s" : ""}`;
    }

    /**
     * @method executeCompleteSearchMailCallBack
     * ═══════════════════════════════════════════════════════════════════════════
     * @private
     */
    private async executeCompleteSearchMailCallBack(
        filter: ViewFilter,
        userRequestId: string,
    ): Promise<{
        userRequestId: string;
        content: Mail[];
    }> {
        let searchResults = await this.mailManager.getViewFilterMails(filter);

        // Synchronise le resultSetCache avec le set complet désormais affiché (« voir plus ») :
        // applyActionToPreviousResults doit porter sur ce que l'utilisateur voit à l'écran.
        // NB : le `refBlock` déjà concaténé à l'historique (singleMessageCache) garde son `n:` initial
        // (ex. n:25) — l'historique est immuable une fois émis. L'IA raisonne donc sur le compte de la
        // 1ʳᵉ page tandis que l'action portera sur le set complet : écart cosmétique assumé, l'invariant
        // produit (« agir sur ce qui est affiché ») prime sur l'exactitude du décompte annoncé.
        const cacheEntry = [...this.resultSetCache.values()].find(
            (entry) => entry.userRequestId === userRequestId,
        );
        if (cacheEntry) {
            cacheEntry.uniqueMailvistaIds = searchResults.map(
                (mail) => mail.uniqueMailvistaId ?? uniqueMailvistaId(mail),
            );
        }

        return {
            userRequestId: userRequestId,
            content: searchResults,
        };
    }

    /**
     * Formate une plage de dates/heures de manière intelligente en français
     * Reproduit le comportement du hook useGetStringifiedDateAndHour
     *
     * @param {Date} startDate - Date/heure de début
     * @param {Date|null} endDate - Date/heure de fin (optionnelle)
     * @returns {string} Plage formatée (ex: "18 septembre à 07:00 → 18 septembre à 07:30")
     */
    private formatFreeTimeSlotDateRange(startDate: Date, endDate: Date) {
        const now = new Date();

        // Normaliser les dates pour comparaison (sans heure)
        const today = new Date(
            now.getFullYear(),
            now.getMonth(),
            now.getDate(),
        );
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        const compareDate = new Date(
            startDate.getFullYear(),
            startDate.getMonth(),
            startDate.getDate(),
        );

        // Formater l'heure au format HH:MM
        const formatTime = (date: Date) => {
            const hours = date.getHours().toString().padStart(2, "0");
            const minutes = date.getMinutes().toString().padStart(2, "0");
            return `${hours}:${minutes}`;
        };

        // Gestion des cas spéciaux : aujourd'hui et hier
        if (compareDate.getTime() === today.getTime()) {
            const startTime = formatTime(startDate);
            if (!endDate) {
                return `Aujourd'hui à ${startTime}`;
            }
            return `Aujourd'hui à ${startTime} → Aujourd'hui à ${formatTime(endDate)}`;
        }

        if (compareDate.getTime() === yesterday.getTime()) {
            const startTime = formatTime(startDate);
            if (!endDate) {
                return `Hier à ${startTime}`;
            }
            return `Hier à ${startTime} → Hier à ${formatTime(endDate)}`;
        }

        // Formater la date de début avec heure
        const startFormatted = `${startDate.toLocaleDateString("fr-FR")} ${formatTime(startDate)}`;

        // Si pas de date de fin, retourner juste le début
        if (!endDate) {
            return startFormatted;
        }

        // Formater l'heure de fin
        const endTime = endDate.toLocaleTimeString("fr-FR", {
            hour: "2-digit",
            minute: "2-digit",
        });

        // Même jour : format "date à HH:MM → HH:MM"
        const endCompareDate = new Date(
            endDate.getFullYear(),
            endDate.getMonth(),
            endDate.getDate(),
        );

        if (compareDate.getTime() === endCompareDate.getTime()) {
            return `${startFormatted} → ${endTime}`;
        }

        // Jours différents : format complet
        const endFormatted = endDate.toLocaleDateString("fr-FR");
        return `${startFormatted} → ${endFormatted}`;
    }

    /**
     * Transforme un array de Mail en array de MinifiedMail
     * @param mails - Array de mails à minifier
     * @param maxCount - Nombre maximum de mails à traiter (optionnel, prend tous si non spécifié)
     * @returns Array de MinifiedMail
     */
    private minifyMails(mails: Mail[], maxCount?: number): MinifiedMail[] {
        logger.info(
            LOGS.ConversationManager,
            `minifyMails() - Processing ${mails.length} mails with maxCount: ${maxCount}`,
        );

        // Limiter le nombre de mails à traiter si maxCount est spécifié
        const mailsToProcess = maxCount ? mails.slice(0, maxCount) : mails;

        const minifiedMails: MinifiedMail[] = mailsToProcess.map((mail) => {
            // Optimisation du Unique Body
            const optimizedUniqueBody: string = optimizeMailBody(
                mail.uniqueBody,
            );

            // Créer l'objet MinifiedMail avec les propriétés requises
            const minifiedMail: MinifiedMail = {
                subject: mail.subject,
                optimizedUniqueBody: optimizedUniqueBody,
                isRead: mail.isRead,
                from: mail.from,
                to: mail.to,
                cc: mail.cc,
                bcc: mail.bcc,
                sentDateTime: mail.sentDateTime,
                isFocus: mail.isFocus,
            };

            // Ajouter les propriétés optionnelles si elles existent
            if (mail.aiDetails?.categories) {
                minifiedMail.categories = mail.aiDetails.categories;
            }

            if (mail.aiDetails?.toDo) {
                minifiedMail.toDo = mail.aiDetails.toDo;
            }

            if (mail.smartLabel) {
                minifiedMail.smartLabel = mail.smartLabel;
            }

            return minifiedMail;
        });

        logger.info(
            LOGS.ConversationManager,
            `minifyMails() - Successfully minified ${minifiedMails.length} mails`,
        );
        return minifiedMails;
    }

    /**
     * Transforme un array d'AgendaItem en array de MinifiedAgenda
     * avec conversion des dates en heure locale française
     * @param agendaItems - Array d'événements d'agenda à minifier
     * @param maxCount - Nombre maximum d'événements à traiter (optionnel, prend tous si non spécifié)
     * @returns Array de MinifiedAgenda
     */
    private minifyAgendaItems(
        agendaItems: AgendaItem[],
        maxCount?: number,
    ): MinifiedAgenda[] {
        logger.info(
            LOGS.ConversationManager,
            `minifyAgendaItems() - Processing ${agendaItems.length} agenda items with maxCount: ${maxCount}`,
        );

        // Limiter le nombre d'événements à traiter si maxCount est spécifié
        const itemsToProcess = maxCount
            ? agendaItems.slice(0, maxCount)
            : agendaItems;

        // Détecter le fuseau horaire et la locale de l'utilisateur
        let userTimeZone: string | undefined;
        let userLocale: string | undefined;
        if (
            typeof Intl !== "undefined" &&
            typeof Intl.DateTimeFormat === "function"
        ) {
            const resolved = Intl.DateTimeFormat().resolvedOptions();
            userTimeZone = resolved.timeZone;
            userLocale = resolved.locale;
        }
        logger.info(
            LOGS.ConversationManager,
            `minifyAgendaItems() - User timezone detected: ${userTimeZone}`,
        );

        const minifiedAgendaItems: MinifiedAgenda[] = itemsToProcess.map(
            (item) => {
                // Fonction pour formater une date en heure locale de l'utilisateur
                // parseGraphDateTimeToIsoString garantit l'ajout du suffixe Z quand timeZone === "UTC",
                // évitant que new Date() interprète la chaîne comme heure locale plutôt qu'UTC.
                const formatToLocal = (
                    agendaDateTime: AgendaItemDateTime,
                ): string => {
                    const isoString =
                        parseGraphDateTimeToIsoString(agendaDateTime);
                    if (!isoString) return "";
                    const date = new Date(isoString);
                    return date.toLocaleString(userLocale, {
                        ...(userTimeZone && { timeZone: userTimeZone }),
                        year: "numeric",
                        month: "2-digit",
                        day: "2-digit",
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: false,
                    });
                };

                // Créer l'objet MinifiedAgenda avec les propriétés requises
                const minifiedAgenda: MinifiedAgenda = {
                    subject: item.subject,
                    bodyPreview: item.bodyPreview,
                    start: formatToLocal(item.start),
                    end: formatToLocal(item.end),
                    attendees: item.attendees,
                    isAllDay: item.isAllDay,
                    isCancelled: item.isCancelled,
                };

                // Ajouter les propriétés optionnelles si elles existent
                if (item.location) {
                    minifiedAgenda.location = item.location;
                }

                if (item.organizer) {
                    minifiedAgenda.organizer = item.organizer;
                }

                if (item.onlineMeeting) {
                    minifiedAgenda.onlineMeeting = item.onlineMeeting;
                }

                return minifiedAgenda;
            },
        );

        logger.info(
            LOGS.ConversationManager,
            `minifyAgendaItems() - Successfully minified ${minifiedAgendaItems.length} agenda items`,
        );
        return minifiedAgendaItems;
    }

    /**
     * Invoque l'agent d'analyse de manière asynchrone
     * @param agentInvokeParams - Paramètres pour l'invocation de l'agent
     */
    private invokeAnalysisAgent(agentInvokeParams: AgentInvokeParams): void {
        logger.info(
            LOGS.ConversationManager,
            `invokeAnalysisAgent() - Invoking analysis agent with sessionId: ${agentInvokeParams.sessionId}`,
        );

        // Appel asynchrone sans await - on laisse l'event gérer le retour
        this.iaManager
            .invokeAnalysisAgent(agentInvokeParams)
            .then((result: string) => {
                logger.info(
                    LOGS.ConversationManager,
                    `invokeAnalysisAgent() - Success for sessionId: ${agentInvokeParams.sessionId}`,
                );
                this.eventBus.emit("analysis-agent-success", {
                    userRequestId: agentInvokeParams.sessionId,
                    result: result,
                });
            })
            .catch((error: any) => {
                logger.error(
                    LOGS.ConversationManager,
                    `invokeAnalysisAgent() - Error for sessionId: ${agentInvokeParams.sessionId}: ${error}`,
                );
                this.eventBus.emit("analysis-agent-error", {
                    userRequestId: agentInvokeParams.sessionId,
                    error: error.message || "Unknown error in analysis agent",
                });
            });
    }

    // #endregion Private Methods
}
